import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputBase, Menu, MenuItem, Select, Snackbar, TextField, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HistoryIcon from '@mui/icons-material/History';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { PRES, TYPE } from '../chrome';
import { IUser } from '../../../types/damTypes';
import { getUserPermissions, hasPermission } from '../../../utils/permissionUtils';
import {
  acceptMediaUpdates,
  addSlide,
  addSlideFromLibrary,
  addTemplateLayout,
  copyTemplateLayout,
  applySlideLayout,
  createGlobalComponent,
  createLibrarySlide,
  createTemplate,
  duplicateTemplate,
  copyOriginReview,
  insertOriginReviewPage,
  createLanguageVersion,
  createPresentationComment,
  createSnapshot,
  deleteGlobalComponent,
  deletePresentation,
  deleteSlide,
  dismissOriginReview,
  deleteTemplateLayout,
  detachGlobalComponent,
  detachLibrarySlide,
  duplicateSlide,
  exportPresentationPdf,
  getLibrarySlide,
  getPresentation,
  getPresentationAsset,
  getTemplate,
  leaveLanguageGroup,
  listGlobalComponents,
  listLibrarySlides,
  listPresentationComments,
  listTemplates,
  openExportedPdf,
  patchLibrarySlide,
  patchPresentation,
  patchSlide,
  patchTemplate,
  patchTemplateLayout,
  pdfDownloadName,
  placeGlobalComponent,
  placePresentationAsset,
  reorderSlides,
  uploadLocalPresentationImage,
  resolvePresentationComment,
  runHealthCheck,
  runPresentationAi,
  runPresentationChat,
  transitionPresentationStatus,
} from '../api';
import LanguageVersionDialog from '../LanguageVersionDialog';
import { familyMembersOf } from '../languageVersionHelpers';
import { COPY, confirmDeleteSlidesText, languageLabel, pageFormatLabel } from '../presentationCopy';
import LanguageVersionBar from './LanguageVersionBar';
import OriginReviewBanner from './OriginReviewBanner';
import GenerateAiBanner from '../generate/GenerateAiBanner';
import { pollGeneration, regenerateGeneration } from '../generate/api';
import OriginReviewPanel from './OriginReviewPanel';
import {
  HealthIssue,
  MediaUpdateItem,
  MediaUpdates,
  PresentationComment,
  PresentationGlobalComponent,
  FieldBinding,
  PresentationLanguage,
  PresentationLibrarySlide,
  PresentationMarket,
  OriginReviewItem,
  PresentationRecord,
  PresentationSlide,
  PresentationStatus,
  PresentationTemplate,
  ChartKind,
  SlideObject,
  SlideObjectStyle,
  LibraryIntent,
  LibrarySlideUpdate,
  VisibleAsset,
  createObjectId,
  defaultLocks,
  PageFormat,
  resolvePageFormat,
  slideSize,
  templatesMatchingPageFormat,
} from '../types';
import { getMountedSlideIndexes } from '../virtualization';
import SlideCanvas from './SlideCanvas';
import SlideStrip from './SlideStrip';
import EditorDock, { EditorDockTab } from './EditorDock';
import { layoutsForNewPage, previewLayout } from './templateLayoutPreview';
import type { PresentationAiChatMessage } from './PresentationAiPanel';
import HealthPanel from './HealthPanel';
import GridView from './GridView';
import HistoryPanel from './HistoryPanel';
import ImportDialog from './ImportDialog';
import StaticSlideView from './StaticSlideView';
import { captureSlideNode } from './slideCapture';
import PresenterView from './PresenterView';
import EditorToolbar, { EditorTool } from './EditorToolbar';
import TemplateModeBanner from './TemplateModeBanner';
import LibraryModeBanner from './LibraryModeBanner';
import LibraryLinkedBanner from './LibraryLinkedBanner';
import ProductLinkedBanner from './ProductLinkedBanner';
import LibraryInsertDialog from './LibraryInsertDialog';
import AddLayoutToKitDialog, { AddLayoutToKitMode } from './AddLayoutToKitDialog';
import { kitsForLayoutTarget } from './kitLayoutTargets';
import LibraryUpdatesDialog from './LibraryUpdatesDialog';
import { isLinkedLibrarySlide, librarySourceMissing } from '../librarySlides';
import { isProductLinkedSlide } from '../pageKind';
import { isSlideLibraryDropTarget, slideLibraryCollision, suggestedLibrarySlideName } from './slideLibraryDrop';
import PdfExportDialog from './PdfExportDialog';
import { applyListHtml, applyLiveList, ListKind } from './listHtml';
import { createLibraryTextObject, LibraryFieldTransfer } from './libraryFieldTransfer';
import { TextStyleStep, textStylePatch, textStyleStepsFromSettings } from './layerList';
import { nudgeOffset } from './objectArrange';
import { canGroup, canMoveSelection, canUngroup, cloneSelection, expandGroupSelection, groupObjects, nextSelection, ungroupObjects } from './objectGroup';
import {
  applyBoxMarkToSelection,
  applyStylePatchToSelection,
  applyTextStyleStepToSelection,
  preferredToolForSelection,
  querySharedTextStyle,
} from './selectionStyle';
import { DEFAULT_TEXT_PADDING, hasEditableText, isCroppableObject, isFillableShape, withShapeTextDefaults } from './shapeFrame';
import { applyPlacedMedia, clearedShapeMedia, filledMediaFromUrl, filledShapeFromUrl, filledShapeObject, fitImageInFrame, resolvePlaceAction, shouldContainPlacedMedia } from './placeTarget';
import { applyTextBoxFit, applyTextBoxFitAll } from './textBoxFit';
import { LibraryCatalogKind } from '../libraryCatalog';
import { stackSelection } from './objectStack';
import {
  applyInlineStyle,
  isBoxLevelTextPatch,
  isExclusiveMarkPatch,
  isInlineTextStylePatch,
  isLiveTextEditor,
  markFromPatch,
  nextLiveTextEditor,
  queryInlineTextStyle,
  rangeHasMark,
  resolveMarkTarget,
  toggleInlineMark,
  type LiveTextEditor,
} from './textInlineMarks';
import { createSlideSaveQueue, SaveStatus } from './slideSaveQueue';
import { canRedo, canUndo, popSlideRedo, popSlideUndo, pushSlideHistory, type SlideHistoryStacks } from './slideHistory';
import { applySlideOrders, moveSlideId, presenterStartIndex, visibleSlides } from './slideOrder';
import { htmlFromAiInsert, sanitizePresentationHtml, stripPresentationText } from './textSanitize';
import { parseClipboardTable } from './excelPaste';
import { clipboardHasFilePayload, readClipboardImageFile } from './clipboardImage';
import {
  applyGridToTable,
  createDefaultTableObject,
  fitTableObjectToPage,
  normalizeTable,
  resizeTableGrid,
  setTableHeaderRow,
  tableFromGrid,
} from './tableModel';
import {
  chartFromGrid,
  createDefaultChartObject,
  normalizeChart,
  resizeChartCategories,
  resizeChartSeries,
  setChartKind,
} from './chartModel';
import StatusMenu from './StatusMenu';
import MediaUpdatesBanner from './MediaUpdatesBanner';
import { guardedSlideDeleteIds, nextSlideSelection, slideIdsForDeleteAction, SlideSelectModifiers } from './slideStripSelection';
import { attachDefaultLinkedFields, createProductLinkedTexts, resolveBoundField, setLinkedFieldSource } from './linkedFields';
import BoundFieldEditDialog from './BoundFieldEditDialog';
import { populateBoundSlide, resolvePopulateTarget, slideHasBindings } from './populateBound';
import { fieldValuesFromAsset } from '../fieldCatalog';
import { adaptUnlockedStyles, canFitObjectInSlot, fitObjectInSlot, wouldAdaptUnlockedStyles } from './smartLayout';
import { isPresentationsAiEnabled } from '../presentationsFlags';
import SettingsGearButton from '../SettingsGearButton';
import { usePresentationSettings } from '../PresentationSettingsContext';
import {
  allowedSettingsHexes,
  hexForSettingsColor,
  settingsLanguageLabel,
  settingsLogicalFields,
  visiblePresentationLanguages,
} from '../presentationSettings';
function cloneSlideObjects(objects: SlideObject[]): SlideObject[] {
  return objects.map((object) => ({
    ...object,
    locks: { ...object.locks },
    style: object.style ? { ...object.style } : object.style,
  }));
}

function libraryAsDeck(item: PresentationLibrarySlide): PresentationRecord {
  return {
    _id: item._id,
    name: item.name,
    ownerUserId: '',
    market: 'SE',
    language: 'sv',
    status: 'draft',
    currentRevision: item.version || 1,
    classification: 'internal',
    pageFormat: item.pageFormat,
    slides: [{
      slideId: item._id,
      presentationId: item._id,
      order: 0,
      layoutId: 'library',
      objects: item.objects || [],
      notes: '',
      backgroundColor: item.backgroundColor || '#FFFFFF',
    }],
  };
}

function templateAsDeck(template: PresentationTemplate): PresentationRecord {
  return {
    _id: template._id,
    name: template.name,
    ownerUserId: '',
    templateId: template._id,
    market: 'SE',
    language: 'sv',
    status: 'draft',
    currentRevision: template.version || 1,
    classification: 'internal',
    pageFormat: template.pageFormat,
    slides: (template.layouts || []).map((layout, index) => ({
      slideId: layout.layoutId,
      presentationId: template._id,
      order: index,
      layoutId: layout.layoutId,
      objects: layout.objects || [],
      notes: '',
      backgroundColor: layout.backgroundColor || '#FFFFFF',
    })),
  };
}

interface PresentationsEditorProps {
  presentationId?: string;
  templateEdit?: { templateId: string; layoutId?: string };
  libraryEdit?: { librarySlideId: string };
  currentUser: IUser | null;
  onBack: () => void;
  onBackLabel?: string;
  onOpenTemplates?: () => void;
  onOpenLibrarySlides?: () => void;
  onEditTemplate?: (templateId: string, layoutId?: string, fromName?: string) => void;
  onOpenPresentation?: (id: string, focus?: { focusSlideId?: string; focusObjectId?: string }) => void;
  onOpenSettings?: () => void;
  focusSlideId?: string;
  focusObjectId?: string;
}

const PresentationsEditor: React.FC<PresentationsEditorProps> = ({
  presentationId, templateEdit, libraryEdit, currentUser, onBack, onBackLabel, onOpenTemplates, onOpenLibrarySlides, onEditTemplate, onOpenPresentation,
  onOpenSettings, focusSlideId, focusObjectId,
}) => {
  const templateMode = Boolean(templateEdit?.templateId);
  const libraryMode = Boolean(libraryEdit?.librarySlideId);
  const specialMode = templateMode || libraryMode;
  const canManageTemplates = hasPermission(currentUser, 'presentations:manageTemplates');
  const canExport = hasPermission(currentUser, 'presentations:export');
  const canCreate = hasPermission(currentUser, 'presentations:create');
  const canEdit = hasPermission(currentUser, 'presentations:edit');
  const canReview = hasPermission(currentUser, 'presentations:review');
  const canApprove = hasPermission(currentUser, 'presentations:approve');
  const canManageSettings = hasPermission(currentUser, 'presentations:admin') || hasPermission(currentUser, 'admin:all');
  const canManageGlobals = hasPermission(currentUser, 'presentations:manageGlobalComponents');
  const canDetachGlobals = hasPermission(currentUser, 'presentations:detachGlobal');
  const { settings } = usePresentationSettings();
  const userPermissions = Array.from(getUserPermissions(currentUser));
  const readOnly = specialMode ? !canManageTemplates : !canEdit;
  const [deck, setDeck] = useState<PresentationRecord | null>(null);
  const [templates, setTemplates] = useState<PresentationTemplate[]>([]);
  const [librarySlides, setLibrarySlides] = useState<PresentationLibrarySlide[]>([]);
  const [libraryBusy, setLibraryBusy] = useState(false);
  const [activeSlideId, setActiveSlideId] = useState<string | null>(null);
  const [selectedSlideIds, setSelectedSlideIds] = useState<string[]>([]);
  const [rangeAnchorId, setRangeAnchorId] = useState<string | null>(null);
  const [selectedObjectIds, setSelectedObjectIds] = useState<string[]>([]);
  const selectedObjectId = selectedObjectIds[selectedObjectIds.length - 1] || null;
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [boundFieldPrompt, setBoundFieldPrompt] = useState<{
    kind: 'edit' | 'ai' | 'insert-ai';
    objectId: string;
    slideId?: string;
    operation?: 'rewrite' | 'translate' | 'checkGrammar';
    instruction?: string;
    html?: string;
  } | null>(null);
  const [isolatedObjectId, setIsolatedObjectId] = useState<string | null>(null);
  const [croppingId, setCroppingId] = useState<string | null>(null);
  const textEditorRef = useRef<LiveTextEditor | null>(null);
  const clipboardRef = useRef<SlideObject[] | null>(null);
  const placeLocalImageRef = useRef<(
    file: File,
    options?: { x?: number; y?: number; forceInsert?: boolean; failedCopy?: string },
  ) => Promise<void>>(async () => undefined);
  const selectedObjectIdsRef = useRef<string[]>([]);
  const isolatedObjectIdRef = useRef<string | null>(null);
  const [advancedMode, setAdvancedMode] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [snap, setSnap] = useState(true);
  const [showSmartGuides, setShowSmartGuides] = useState(true);
  const [notes, setNotes] = useState('');
  const [saveState, setSaveState] = useState<SaveStatus>('saved');
  const [libraryIntent, setLibraryIntent] = useState<LibraryIntent>('insert-image');
  const [revealAssetId, setRevealAssetId] = useState<string | null>(null);
  const [revealNonce, setRevealNonce] = useState(0);
  const [layoutConfirm, setLayoutConfirm] = useState<string | null>(null);
  const [addingLayout, setAddingLayout] = useState(false);
  const [changeTemplateOpen, setChangeTemplateOpen] = useState(false);
  const [changeTemplateId, setChangeTemplateId] = useState('');
  const [renameLayoutOpen, setRenameLayoutOpen] = useState(false);
  const [renameLayoutValue, setRenameLayoutValue] = useState('');
  const [kitLayoutDialog, setKitLayoutDialog] = useState<{
    mode: AddLayoutToKitMode;
    slideId?: string;
    sourceTemplateId?: string;
    layoutId?: string;
  } | null>(null);
  const [kitLayoutName, setKitLayoutName] = useState('');
  const [kitLayoutKitId, setKitLayoutKitId] = useState('');
  const [kitLayoutBusy, setKitLayoutBusy] = useState(false);
  const [kitLayoutError, setKitLayoutError] = useState<string | null>(null);
  const [historyTick, setHistoryTick] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [healthOpen, setHealthOpen] = useState(false);
  const [health, setHealth] = useState<{ readyBlocked: boolean; issues: HealthIssue[]; aiEnabled?: boolean }>({ readyBlocked: false, issues: [] });
  const [mediaUpdates, setMediaUpdates] = useState<MediaUpdates>({ applied: [], available: [] });
  const [comments, setComments] = useState<PresentationComment[]>([]);
  const [globalComponents, setGlobalComponents] = useState<PresentationGlobalComponent[]>([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [regenerateBusy, setRegenerateBusy] = useState(false);
  const regenerateAbortRef = useRef<AbortController | null>(null);
  const [translateLanguage, setTranslateLanguage] = useState<PresentationLanguage>('sv');
  const [aiChatMessages, setAiChatMessages] = useState<PresentationAiChatMessage[]>([]);
  const [dockTab, setDockTab] = useState<EditorDockTab>('properties');
  const [activeTool, setActiveTool] = useState<EditorTool | null>(null);
  const [workspaceMode, setWorkspaceMode] = useState<'edit' | 'overview'>('edit');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importKind, setImportKind] = useState<'pptx' | 'pdf'>('pptx');
  const [presenting, setPresenting] = useState(false);
  const [gridSelected, setGridSelected] = useState<string[]>([]);
  const [snack, setSnack] = useState<string | null>(null);
  const [insertLibraryId, setInsertLibraryId] = useState<string | null>(null);
  const [libraryUpdates, setLibraryUpdates] = useState<LibrarySlideUpdate[]>([]);
  const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null);
  const [printSlide, setPrintSlide] = useState<PresentationSlide | null>(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const printNodeRef = useRef<HTMLDivElement | null>(null);
  const [languageVersionOpen, setLanguageVersionOpen] = useState(false);
  const [languageVersionBusy, setLanguageVersionBusy] = useState(false);
  const [languageVersionError, setLanguageVersionError] = useState<string | null>(null);
  const [originReviewItem, setOriginReviewItem] = useState<OriginReviewItem | null>(null);
  const [originReviewBusy, setOriginReviewBusy] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const historyRef = useRef<Map<string, SlideHistoryStacks<SlideObject[]>>>(new Map());
  const deckRef = useRef<PresentationRecord | null>(null);
  const presentationIdRef = useRef<string | undefined>(presentationId);
  const activeSlideIdRef = useRef<string | null>(null);
  const selectedSlideIdsRef = useRef<string[]>([]);
  const rangeAnchorIdRef = useRef<string | null>(null);
  const pendingSelectObjectIdRef = useRef<string | null>(null);
  const pendingFocusSlideIdRef = useRef<string | null>(null);
  deckRef.current = deck;
  presentationIdRef.current = presentationId;
  selectedSlideIdsRef.current = selectedSlideIds;
  rangeAnchorIdRef.current = rangeAnchorId;

  const saveQueue = useMemo(() => createSlideSaveQueue({
    save: async (slideId, patch) => {
      if (libraryEdit?.librarySlideId) {
        await patchLibrarySlide(libraryEdit.librarySlideId, {
          objects: Array.isArray(patch.objects) ? patch.objects as SlideObject[] : undefined,
          backgroundColor: typeof patch.backgroundColor === 'string' ? patch.backgroundColor : undefined,
        });
        return;
      }
      if (templateEdit?.templateId) {
        await patchTemplateLayout(templateEdit.templateId, slideId, {
          objects: Array.isArray(patch.objects) ? patch.objects as SlideObject[] : undefined,
          backgroundColor: typeof patch.backgroundColor === 'string' ? patch.backgroundColor : undefined,
        });
        return;
      }
      if (!presentationId) return;
      await patchSlide(presentationId, slideId, patch as Parameters<typeof patchSlide>[2]);
    },
    onStatus: setSaveState,
    onError: (message) => setError(message),
  }), [libraryEdit?.librarySlideId, presentationId, templateEdit?.templateId]);

  useEffect(() => () => saveQueue.dispose(), [saveQueue]);

  const slides = deck?.slides || [];
  const activeIndex = Math.max(0, slides.findIndex((slide) => slide.slideId === activeSlideId));
  const activeSlide = slides[activeIndex] || slides[0] || null;
  activeSlideIdRef.current = activeSlide?.slideId || null;
  const libraryLocked = !libraryMode && isLinkedLibrarySlide(activeSlide);
  const canvasReadOnly = readOnly || libraryLocked;
  const sourceMissing = !libraryMode && librarySourceMissing(activeSlide, librarySlides);
  const mountedIndexes = useMemo(() => getMountedSlideIndexes(activeIndex, slides.length, 1), [activeIndex, slides.length]);

  const load = useCallback(async (options?: { announceLibraryUpdates?: boolean }) => {
    if (libraryEdit?.librarySlideId) {
      const item = await getLibrarySlide(libraryEdit.librarySlideId);
      const nextDeck = libraryAsDeck(item);
      setDeck(nextDeck);
      setLibrarySlides([item]);
      setAdvancedMode(true);
      setActiveSlideId(item._id);
      setSelectedSlideIds([]);
      setRangeAnchorId(null);
      setNotes('');
      return;
    }
    if (templateEdit?.templateId) {
      const [template, nextTemplates] = await Promise.all([
        getTemplate(templateEdit.templateId),
        listTemplates().catch(() => [] as PresentationTemplate[]),
      ]);
      const nextDeck = templateAsDeck(template);
      setDeck(nextDeck);
      setTemplates(
        nextTemplates.some((item) => item._id === template._id)
          ? nextTemplates
          : [template, ...nextTemplates],
      );
      setAdvancedMode(true);
      setActiveSlideId((current) => {
        const ids = new Set((nextDeck.slides || []).map((slide) => slide.slideId));
        if (templateEdit.layoutId && ids.has(templateEdit.layoutId)) return templateEdit.layoutId;
        if (current && ids.has(current)) return current;
        return nextDeck.slides?.[0]?.slideId || null;
      });
      setSelectedSlideIds((current) => {
        const ids = new Set((nextDeck.slides || []).map((slide) => slide.slideId));
        return current.filter((id) => ids.has(id));
      });
      setRangeAnchorId((current) => {
        const ids = new Set((nextDeck.slides || []).map((slide) => slide.slideId));
        return current && ids.has(current) ? current : null;
      });
      const nextId = templateEdit.layoutId
        || (activeSlideIdRef.current && (nextDeck.slides || []).some((slide) => slide.slideId === activeSlideIdRef.current)
          ? activeSlideIdRef.current
          : nextDeck.slides?.[0]?.slideId || null);
      const nextNotes = (nextDeck.slides || []).find((slide) => slide.slideId === nextId)?.notes || '';
      setNotes(nextNotes);
      return;
    }
    if (!presentationId) return;
    const requestedId = presentationId;
    const [nextDeck, nextTemplates, nextLibrary] = await Promise.all([
      getPresentation(requestedId),
      listTemplates(),
      listLibrarySlides().catch(() => [] as PresentationLibrarySlide[]),
    ]);
    if (presentationIdRef.current !== requestedId) return;
    setLibrarySlides(nextLibrary);
    if (nextDeck.templateId && !nextTemplates.some((template) => template._id === nextDeck.templateId)) {
      try {
        nextTemplates.push(await getTemplate(nextDeck.templateId));
      } catch {
        /* archived or missing template is still usable if already on the deck */
      }
    }
    setDeck(nextDeck);
    setTemplates(nextTemplates);
    setActiveSlideId((current) => {
      const ids = new Set((nextDeck.slides || []).map((slide) => slide.slideId));
      const focusId = pendingFocusSlideIdRef.current;
      if (focusId && ids.has(focusId)) {
        pendingFocusSlideIdRef.current = null;
        return focusId;
      }
      if (current && ids.has(current)) return current;
      return nextDeck.slides?.[0]?.slideId || null;
    });
    setSelectedSlideIds((current) => {
      const ids = new Set((nextDeck.slides || []).map((slide) => slide.slideId));
      return current.filter((id) => ids.has(id));
    });
    setRangeAnchorId((current) => {
      const ids = new Set((nextDeck.slides || []).map((slide) => slide.slideId));
      return current && ids.has(current) ? current : null;
    });
    const nextId = activeSlideIdRef.current && (nextDeck.slides || []).some((slide) => slide.slideId === activeSlideIdRef.current)
      ? activeSlideIdRef.current
      : nextDeck.slides?.[0]?.slideId || null;
    const nextNotes = (nextDeck.slides || []).find((slide) => slide.slideId === nextId)?.notes || '';
    setNotes(nextNotes);
    setMediaUpdates(nextDeck.mediaUpdates || { applied: [], available: [] });
    if (options?.announceLibraryUpdates) {
      setLibraryUpdates(nextDeck.libraryUpdates || []);
    }
    setHealth((current) => ({ ...current, aiEnabled: Boolean(nextDeck.aiEnabled) }));
  }, [libraryEdit?.librarySlideId, presentationId, templateEdit?.templateId, templateEdit?.layoutId]);

  const currentTemplate = useMemo(() => (
    templates.find((template) => template._id === (deck?.templateId || templateEdit?.templateId)) || templates[0] || null
  ), [templates, deck?.templateId, templateEdit?.templateId]);
  const pageSize = useMemo(() => slideSize(deck?.pageFormat), [deck?.pageFormat]);
  const isA4 = resolvePageFormat(deck?.pageFormat) === 'a4_portrait';

  const reloadComments = useCallback(async (slideId?: string) => {
    const id = slideId || activeSlideIdRef.current;
    if (!id || !presentationId || specialMode) return;
    setComments(await listPresentationComments(presentationId, id));
  }, [presentationId, specialMode]);

  useEffect(() => {
    if (activeSlide?.slideId) void reloadComments(activeSlide.slideId).catch(() => setComments([]));
  }, [activeSlide?.slideId, reloadComments]);

  useEffect(() => {
    void listGlobalComponents().then(setGlobalComponents).catch(() => setGlobalComponents([]));
  }, []);

  useEffect(() => { void load({ announceLibraryUpdates: true }).catch((err) => setError(err?.response?.data?.message || 'Kunde inte öppna presentationen.')); }, [load]);

  useEffect(() => {
    if (deck?.language) setTranslateLanguage(deck.language);
  }, [deck?.language]);

  useEffect(() => {
    historyRef.current.clear();
    setHistoryTick((tick) => tick + 1);
    setLanguageVersionOpen(false);
    setLanguageVersionError(null);
    setOriginReviewItem(null);
    pendingFocusSlideIdRef.current = focusSlideId || null;
    if (focusObjectId) pendingSelectObjectIdRef.current = focusObjectId;
    setSelectedObjectIds([]);
    setEditingTextId(null);
    setIsolatedObjectId(null);
    if (presentationId) setDeck(null);
  }, [presentationId, focusSlideId, focusObjectId]);

  useEffect(() => {
    if (specialMode && (dockTab === 'comments' || dockTab === 'ai' || dockTab === 'components' || dockTab === 'slideLibrary' || dockTab === 'templates')) {
      setDockTab('properties');
    }
  }, [specialMode, dockTab]);

  useEffect(() => {
    setAiChatMessages([]);
  }, [presentationId]);

  useEffect(() => {
    setNotes(activeSlide?.notes || '');
    const pendingId = pendingSelectObjectIdRef.current;
    pendingSelectObjectIdRef.current = null;
    isolatedObjectIdRef.current = null;
    setIsolatedObjectId(null);
    setEditingTextId(null);
    textEditorRef.current = null;
    setCroppingId(null);
    if (pendingId && (activeSlide?.objects || []).some((object) => object.id === pendingId)) {
      selectedObjectIdsRef.current = [pendingId];
      setSelectedObjectIds([pendingId]);
      isolatedObjectIdRef.current = pendingId;
      setIsolatedObjectId(pendingId);
      setDockTab('properties');
      return;
    }
    selectedObjectIdsRef.current = [];
    setSelectedObjectIds([]);
    // Only reset local editor chrome when the active slide changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlide?.slideId]);

  const replaceSlide = (slideId: string, patch: Partial<PresentationSlide>) => {
    setDeck((current) => {
      if (!current) return current;
      const next = {
        ...current,
        slides: (current.slides || []).map((slide) => slide.slideId === slideId ? { ...slide, ...patch } : slide),
      };
      deckRef.current = next;
      return next;
    });
  };

  const persistObjects = (slideId: string, objects: SlideObject[], immediate = false) => {
    if (readOnly) return;
    const current = (deckRef.current?.slides || []).find((slide) => slide.slideId === slideId);
    if (current?.locked) return;
    if (!libraryMode && isLinkedLibrarySlide(current)) return;
    replaceSlide(slideId, { objects });
    saveQueue.schedule(slideId, { objects }, immediate);
  };

  const persistNotes = (value: string) => {
    if (!activeSlide || readOnly || specialMode) return;
    setNotes(value);
    replaceSlide(activeSlide.slideId, { notes: value });
    saveQueue.schedule(activeSlide.slideId, { notes: value });
  };

  const beginHistory = () => {
    if (!activeSlide || readOnly) return;
    pushSlideHistory(historyRef.current, activeSlide.slideId, cloneSlideObjects(activeSlide.objects));
    setHistoryTick((value) => value + 1);
  };

  const commitLiveText = () => {
    const live = textEditorRef.current;
    const slide = deckRef.current?.slides?.find((item) => item.slideId === activeSlideIdRef.current);
    if (!slide || !isLiveTextEditor(live)) return;
    const html = sanitizePresentationHtml(live.editor.getHTML());
    const object = slide.objects.find((item) => item.id === live.objectId);
    if (!object || object.content === html) return;
    persistObjects(slide.slideId, slide.objects.map((item) => (
      item.id === object.id ? { ...item, content: html } : item
    )));
  };

  const flushAll = useCallback(async () => {
    commitLiveText();
    await saveQueue.flushAll();
  // commitLiveText reads refs and the current save queue only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveQueue]);

  const applySlideSelection = useCallback((next: { selectedIds: string[]; activeId: string; rangeAnchorId: string }) => {
    setActiveSlideId(next.activeId);
    setSelectedSlideIds(next.selectedIds);
    setRangeAnchorId(next.rangeAnchorId);
  }, []);

  const selectSlide = useCallback(async (slideId: string) => {
    await flushAll();
    applySlideSelection({ selectedIds: [slideId], activeId: slideId, rangeAnchorId: slideId });
  }, [applySlideSelection, flushAll]);

  const handleSelectSlide = useCallback((slideId: string, modifiers?: SlideSelectModifiers) => {
    const ordered = (deckRef.current?.slides || []).map((slide) => slide.slideId);
    const next = nextSlideSelection(
      ordered,
      selectedSlideIdsRef.current,
      activeSlideIdRef.current,
      slideId,
      modifiers || {},
      rangeAnchorIdRef.current,
    );
    void (async () => {
      await flushAll();
      applySlideSelection(next);
    })();
  }, [applySlideSelection, flushAll]);

  const addSlideFromLayout = useCallback(async (layoutId?: string, sourceTemplateId?: string) => {
    if (readOnly) return;
    await flushAll();
    if (templateEdit?.templateId) {
      const layout = await addTemplateLayout(templateEdit.templateId, { name: COPY.newLayout });
      await load();
      applySlideSelection({ selectedIds: [layout.layoutId], activeId: layout.layoutId, rangeAnchorId: layout.layoutId });
      return;
    }
    if (!presentationId) return;
    setAddingLayout(true);
    try {
      const slide = await addSlide(presentationId, layoutId, sourceTemplateId);
      await load();
      applySlideSelection({ selectedIds: [slide.slideId], activeId: slide.slideId, rangeAnchorId: slide.slideId });
    } catch (err: any) {
      setError(err?.response?.data?.message || COPY.pageFormatAddSlideMismatch);
    } finally {
      setAddingLayout(false);
    }
  }, [applySlideSelection, flushAll, load, presentationId, readOnly, templateEdit?.templateId]);

  const requestInsertFromLibrary = useCallback((librarySlideId: string) => {
    if (readOnly || !presentationId || libraryMode) return;
    setInsertLibraryId(librarySlideId);
  }, [libraryMode, presentationId, readOnly]);

  const addSlideFromLibraryPage = useCallback(async (librarySlideId: string, follow: boolean) => {
    if (readOnly || !presentationId || libraryMode) return;
    await flushAll();
    setAddingLayout(true);
    try {
      const slide = await addSlideFromLibrary(presentationId, librarySlideId, { follow });
      setInsertLibraryId(null);
      await load();
      applySlideSelection({ selectedIds: [slide.slideId], activeId: slide.slideId, rangeAnchorId: slide.slideId });
      setSnack(follow ? COPY.libraryInsertFollowDone : COPY.libraryInsertOwnDone);
    } catch (err: any) {
      setError(err?.response?.data?.message || COPY.libraryPageFormatMismatch);
    } finally {
      setAddingLayout(false);
    }
  }, [applySlideSelection, flushAll, libraryMode, load, presentationId, readOnly]);

  const handleDetachLibrary = useCallback(async () => {
    if (!presentationId || !activeSlide || libraryMode || readOnly) return;
    setLibraryBusy(true);
    try {
      await flushAll();
      const slide = await detachLibrarySlide(presentationId, activeSlide.slideId);
      replaceSlide(activeSlide.slideId, {
        libraryDetached: slide.libraryDetached,
        librarySlideId: slide.librarySlideId,
        objects: slide.objects,
        backgroundColor: slide.backgroundColor,
      });
      setSnack(COPY.libraryDetached);
    } catch (err: any) {
      setError(err?.response?.data?.message || COPY.saveError);
    } finally {
      setLibraryBusy(false);
    }
  }, [activeSlide, flushAll, libraryMode, presentationId, readOnly]);

  const pickerTemplates = useMemo(
    () => templatesMatchingPageFormat(
      (templates || []).filter((template) => template.isActive !== false),
      deck?.pageFormat,
    ),
    [templates, deck?.pageFormat],
  );

  const reloadPickerTemplates = useCallback(async () => {
    const next = await listTemplates();
    if (deck?.templateId && !next.some((template) => template._id === deck.templateId)) {
      try {
        next.push(await getTemplate(deck.templateId));
      } catch {
        /* archived mall still used by this presentation */
      }
    }
    setTemplates(next);
  }, [deck?.templateId]);

  const handleAddSlide = useCallback(async () => {
    if (readOnly || libraryMode) return;
    if (templateMode) {
      await addSlideFromLayout();
      return;
    }
    const usable = pickerTemplates.flatMap((template) => (
      layoutsForNewPage(template.layouts).map((layout) => ({ template, layout }))
    ));
    if (usable.length <= 1) {
      await addSlideFromLayout(usable[0]?.layout.layoutId, usable[0]?.template._id);
      return;
    }
    setDockTab('templates');
  }, [addSlideFromLayout, libraryMode, pickerTemplates, readOnly, templateMode]);

  useEffect(() => {
    const onUnload = (event: BeforeUnloadEvent) => {
      if (saveQueue.hasUnsaved()) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [saveQueue]);

  const commitGesture = (nextObjects: SlideObject[]) => {
    const slideId = activeSlideIdRef.current;
    if (!slideId || readOnly) return;
    persistObjects(slideId, nextObjects);
  };

  const applySelectionTool = (objects: SlideObject[], ids: string[]) => {
    const tool = preferredToolForSelection(objects, ids);
    if (tool === 'text') setActiveTool('text');
    else if (tool === 'image') setActiveTool('image');
    else if (tool === 'table') setActiveTool('table');
    else if (tool === 'chart') setActiveTool('chart');
    else setActiveTool(null);
  };

  const selectObject = useCallback((objectId: string | null, options?: { additive?: boolean; isolate?: boolean; ids?: string[] }) => {
    setDockTab('properties');
    const slide = deckRef.current?.slides?.find((item) => item.slideId === activeSlideIdRef.current);
    const objects = slide?.objects || [];
    if (options?.ids) {
      isolatedObjectIdRef.current = null;
      setIsolatedObjectId(null);
      const wanted = new Set(options.ids);
      const next = objects.filter((item) => wanted.has(item.id)).map((item) => item.id);
      selectedObjectIdsRef.current = next;
      setSelectedObjectIds(next);
      applySelectionTool(objects, next);
      return;
    }
    if (!objectId) {
      isolatedObjectIdRef.current = null;
      setIsolatedObjectId(null);
      selectedObjectIdsRef.current = [];
      setSelectedObjectIds([]);
      return;
    }
    const target = objects.find((item) => item.id === objectId);
    const currentIsolated = isolatedObjectIdRef.current
      ? objects.find((item) => item.id === isolatedObjectIdRef.current)
      : null;
    const isolate = Boolean(options?.isolate)
      || Boolean(!options?.additive && currentIsolated?.groupId && target?.groupId && currentIsolated.groupId === target.groupId);
    if (isolate) {
      isolatedObjectIdRef.current = objectId;
      setIsolatedObjectId(objectId);
      selectedObjectIdsRef.current = [objectId];
      setSelectedObjectIds([objectId]);
    } else {
      isolatedObjectIdRef.current = null;
      setIsolatedObjectId(null);
      const next = nextSelection(objects, selectedObjectIdsRef.current, objectId, options);
      selectedObjectIdsRef.current = next;
      setSelectedObjectIds(next);
    }
    const primaryId = isolate ? objectId : (selectedObjectIdsRef.current[selectedObjectIdsRef.current.length - 1] || null);
    if (!primaryId) return;
    applySelectionTool(objects, selectedObjectIdsRef.current);
  }, []);

  const selectCreatedObject = useCallback((objectId: string, editingText = false) => {
    isolatedObjectIdRef.current = null;
    setIsolatedObjectId(null);
    selectedObjectIdsRef.current = [objectId];
    setSelectedObjectIds([objectId]);
    setEditingTextId(editingText ? objectId : null);
    setDockTab('properties');
  }, []);

  const changeObjects = (objects: SlideObject[]) => {
    if (!activeSlide || readOnly || libraryLocked) return;
    beginHistory();
    persistObjects(activeSlide.slideId, objects);
  };

  const applyBoundFieldLocal = (objectId: string, slideId?: string, prepareShape = false, changedHtml?: string) => {
    const targetSlideId = slideId || activeSlideIdRef.current;
    if (!targetSlideId || readOnly) return false;
    const slide = (deckRef.current?.slides || []).find((item) => item.slideId === targetSlideId);
    if (!slide || (!libraryMode && isLinkedLibrarySlide(slide))) return false;
    const object = slide.objects.find((item) => item.id === objectId);
    const bound = resolveBoundField(slide.objects, object);
    if (!object || !bound || bound.source !== 'direct') return false;
    const changedContent = changedHtml === undefined ? undefined : sanitizePresentationHtml(changedHtml);
    let next = setLinkedFieldSource(
      slide.objects,
      bound.productId,
      bound.key,
      'local',
      changedContent ?? object.content,
    );
    if (changedContent !== undefined) {
      next = next.map((item) => (
        item.id === objectId
          ? applyTextBoxFit({ ...item, content: changedContent }, changedContent, pageSize)
          : item
      ));
    }
    if (prepareShape) {
      const target = next.find((item) => item.id === objectId);
      if (target && isFillableShape(target) && !target.style?.fontFamily) {
        next = next.map((item) => item.id === objectId ? withShapeTextDefaults(item) : item);
      }
    }
    flushSync(() => {
      if (targetSlideId === activeSlideIdRef.current) {
        beginHistory();
        persistObjects(targetSlideId, next);
      } else {
        persistObjects(targetSlideId, next, true);
      }
    });
    return true;
  };

  const shouldPromptBoundField = (object?: SlideObject | null, objects?: SlideObject[]) => {
    if (specialMode || canvasReadOnly) return false;
    return resolveBoundField(objects || [], object)?.source === 'direct';
  };

  const applyUndo = async () => {
    if (!activeSlide || readOnly) return;
    await flushAll();
    const current = deckRef.current?.slides?.find((slide) => slide.slideId === activeSlide.slideId) || activeSlide;
    const result = popSlideUndo(historyRef.current, current.slideId, cloneSlideObjects(current.objects));
    if (!result) return;
    setEditingTextId(null);
    textEditorRef.current = null;
    isolatedObjectIdRef.current = null;
    setIsolatedObjectId(null);
    persistObjects(current.slideId, result.next, true);
    setHistoryTick((value) => value + 1);
  };

  const applyRedo = async () => {
    if (!activeSlide || readOnly) return;
    await flushAll();
    const current = deckRef.current?.slides?.find((slide) => slide.slideId === activeSlide.slideId) || activeSlide;
    const result = popSlideRedo(historyRef.current, current.slideId, cloneSlideObjects(current.objects));
    if (!result) return;
    setEditingTextId(null);
    textEditorRef.current = null;
    isolatedObjectIdRef.current = null;
    setIsolatedObjectId(null);
    persistObjects(current.slideId, result.next, true);
    setHistoryTick((value) => value + 1);
  };

  selectedObjectIdsRef.current = selectedObjectIds;
  isolatedObjectIdRef.current = isolatedObjectId;
  const selectedObject = activeSlide?.objects.find((object) => object.id === selectedObjectId) || null;
  const selectedObjects = (activeSlide?.objects || []).filter((object) => selectedObjectIds.includes(object.id));
  const croppableSelected = selectedObjects.find((object) => isCroppableObject(object)) || null;

  useEffect(() => {
    if (!advancedMode && dockTab === 'layers') setDockTab('properties');
  }, [advancedMode, dockTab]);

  const openLibrary = (intent: LibraryIntent = 'insert-image') => {
    setLibraryIntent(intent);
    setDockTab('library');
  };

  const persistInlineHtml = (html: string) => {
    if (!activeSlide || !selectedObject) return;
    const content = sanitizePresentationHtml(html);
    changeObjects(activeSlide.objects.map((object) => {
      if (object.id !== selectedObject.id) return object;
      return applyTextBoxFit({ ...object, content }, content, pageSize);
    }));
  };

  const persistTextBoxFit = (objectId: string, html: string) => {
    const slide = deckRef.current?.slides?.find((item) => item.slideId === activeSlideIdRef.current);
    if (!slide || readOnly) return;
    const object = slide.objects.find((item) => item.id === objectId);
    if (!object) return;
    const fitted = applyTextBoxFit(object, html, pageSize);
    if (fitted.w === object.w && fitted.h === object.h) return;
    persistObjects(slide.slideId, slide.objects.map((item) => (
      item.id === objectId ? { ...item, w: fitted.w, h: fitted.h } : item
    )));
  };

  const syncLiveContent = (objects: SlideObject[]) => {
    const live = textEditorRef.current;
    if (!isLiveTextEditor(live)) return;
    const updated = objects.find((object) => object.id === live.objectId);
    if (updated && (updated.content || '') !== live.editor.getHTML()) {
      live.editor.commands.setContent(updated.content || '', false);
    }
  };

  const applyBoxMark = (mark: 'bold' | 'italic' | 'underline') => {
    if (!activeSlide || !selectedObject || !hasEditableText(selectedObject)) return;
    const next = applyTextBoxFitAll(applyBoxMarkToSelection(activeSlide.objects, selectedObjectIds, mark, selectedObject.id), selectedObjectIds, pageSize);
    syncLiveContent(next);
    changeObjects(next);
  };

  const toggleSelectedMark = (mark: 'bold' | 'italic' | 'underline') => {
    if (!activeSlide || !selectedObject || !hasEditableText(selectedObject)) return;
    const live = textEditorRef.current;
    const target = resolveMarkTarget(editingTextId, selectedObject.id, live);
    if (target === 'box') {
      applyBoxMark(mark);
      return;
    }
    if (target !== 'inline' || !isLiveTextEditor(live, selectedObject.id)) return;
    if (!toggleInlineMark(live.editor, mark)) return;
    persistInlineHtml(live.editor.getHTML());
  };

  const querySelectedMark = (mark: 'bold' | 'italic' | 'underline'): boolean | null => {
    if (!selectedObject || !hasEditableText(selectedObject)) return null;
    const live = textEditorRef.current;
    const target = resolveMarkTarget(editingTextId, selectedObject.id, live);
    if (target === 'inline' && isLiveTextEditor(live, selectedObject.id)) {
      return rangeHasMark(live.editor, mark);
    }
    if (target === 'none') return null;
    if (mark === 'bold') return Number(selectedObject.style?.fontWeight || 0) === 700;
    if (mark === 'italic') return selectedObject.style?.fontStyle === 'italic';
    return selectedObject.style?.textDecoration === 'underline';
  };

  const applySelectedStyle = (patch: Record<string, unknown>) => {
    if (!activeSlide || !selectedObjectIds.length || readOnly) return;
    const live = textEditorRef.current;

    if (isExclusiveMarkPatch(patch)) {
      const mark = markFromPatch(patch);
      if (mark) toggleSelectedMark(mark);
      return;
    }

    const editingSingle = Boolean(
      selectedObject
      && selectedObjectIds.length === 1
      && hasEditableText(selectedObject)
      && editingTextId === selectedObject.id
      && !isBoxLevelTextPatch(patch)
    );
    if (editingSingle && selectedObject && isLiveTextEditor(live, selectedObject.id) && isInlineTextStylePatch(patch) && applyInlineStyle(live.editor, patch)) {
      persistInlineHtml(live.editor.getHTML());
      return;
    }

    const next = applyTextBoxFitAll(applyStylePatchToSelection(activeSlide.objects, selectedObjectIds, patch), selectedObjectIds, pageSize);
    syncLiveContent(next);
    changeObjects(next);
  };

  const querySelectedStyle = (key: 'fontFamily' | 'fontSize' | 'color' | 'fontWeight') => {
    if (!selectedObjectIds.length) return { value: null, mixed: false };
    const live = textEditorRef.current;
    if (
      selectedObject
      && selectedObjectIds.length === 1
      && editingTextId === selectedObject.id
      && isLiveTextEditor(live, selectedObject.id)
    ) {
      return queryInlineTextStyle(live.editor, key);
    }
    return querySharedTextStyle(activeSlide?.objects || [], selectedObjectIds, key);
  };

  const removeSelectedObject = () => {
    if (!activeSlide || !selectedObjects.length) return;
    const removable = new Set(selectedObjects.filter((object) => !object.locks.delete).map((object) => object.id));
    if (!removable.size) return;
    changeObjects(activeSlide.objects.filter((object) => !removable.has(object.id)));
    selectObject(null);
  };

  const stackSelected = (direction: 'front' | 'back') => {
    if (!activeSlide || readOnly || !selectedObjectIds.length) return;
    changeObjects(stackSelection(activeSlide.objects, selectedObjectIds, direction));
  };

  const groupSelected = () => {
    if (!activeSlide || readOnly || !canGroup(selectedObjectIds)) return;
    setEditingTextId(null);
    isolatedObjectIdRef.current = null;
    setIsolatedObjectId(null);
    changeObjects(groupObjects(activeSlide.objects, selectedObjectIds));
  };

  const ungroupSelected = () => {
    if (!activeSlide || readOnly || !canUngroup(activeSlide.objects, selectedObjectIds)) return;
    changeObjects(ungroupObjects(activeSlide.objects, selectedObjectIds));
  };

  const revealInLibrary = (assetId: string) => {
    setRevealAssetId(assetId);
    setRevealNonce((value) => value + 1);
    setLibraryIntent('insert-image');
    setDockTab('library');
  };

  const removeSlides = useCallback(async (requestedIds: string[]) => {
    if (readOnly || libraryMode) return;
    const ordered = (deckRef.current?.slides || []).map((slide) => slide.slideId);
    const locked = (deckRef.current?.slides || [])
      .filter((slide) => slide.locked)
      .map((slide) => slide.slideId);
    const guarded = guardedSlideDeleteIds(ordered, requestedIds, locked);
    if (guarded.reason === 'locked') {
      setSnack(COPY.cannotDeleteLocked);
      return;
    }
    if (guarded.reason === 'keepLast') {
      setSnack(templateMode ? COPY.keepOneLayout : COPY.keepOneSlide);
      return;
    }
    if (!window.confirm(confirmDeleteSlidesText(guarded.ids.length, templateMode))) return;
    try {
      await flushAll();
      for (const slideId of guarded.ids) {
        if (templateEdit?.templateId) {
          await deleteTemplateLayout(templateEdit.templateId, slideId);
        } else if (presentationId) {
          await deleteSlide(presentationId, slideId);
        }
      }
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || COPY.deleteFailed);
    }
  }, [flushAll, libraryMode, load, presentationId, readOnly, templateEdit?.templateId, templateMode]);

  const handleDeleteSlide = useCallback((slideId: string) => {
    const ordered = (deckRef.current?.slides || []).map((slide) => slide.slideId);
    void removeSlides(slideIdsForDeleteAction(ordered, selectedSlideIdsRef.current, slideId));
  }, [removeSlides]);

  const handleDuplicateSlide = useCallback(async (slideId: string) => {
    if (readOnly || libraryMode) return;
    try {
      await flushAll();
      if (templateEdit?.templateId) {
        const source = (deckRef.current?.slides || []).find((slide) => slide.slideId === slideId);
        const layout = await addTemplateLayout(templateEdit.templateId, {
          name: source ? `${currentTemplate?.layouts.find((item) => item.layoutId === slideId)?.name || COPY.newLayout} copy` : COPY.newLayout,
          objects: source?.objects,
        });
        await load();
        applySlideSelection({ selectedIds: [layout.layoutId], activeId: layout.layoutId, rangeAnchorId: layout.layoutId });
        return;
      }
      if (!presentationId) return;
      const slide = await duplicateSlide(presentationId, slideId);
      await load();
      applySlideSelection({ selectedIds: [slide.slideId], activeId: slide.slideId, rangeAnchorId: slide.slideId });
    } catch (err: any) {
      setError(err?.response?.data?.message || COPY.duplicateFailed);
    }
  }, [applySlideSelection, currentTemplate, flushAll, libraryMode, load, presentationId, readOnly, templateEdit?.templateId]);

  const handleToggleHidden = useCallback(async (slideId: string) => {
    if (readOnly || specialMode || !presentationId) return;
    const slide = (deckRef.current?.slides || []).find((item) => item.slideId === slideId);
    if (!slide) return;
    const hidden = !slide.hidden;
    try {
      await flushAll();
      replaceSlide(slideId, { hidden });
      await patchSlide(presentationId, slideId, { hidden });
    } catch (err: any) {
      await load();
      setError(err?.response?.data?.message || COPY.saveError);
    }
  }, [flushAll, load, presentationId, readOnly]);

  const handleToggleLock = useCallback(async (slideId: string) => {
    if (readOnly || specialMode || !presentationId) return;
    const slide = (deckRef.current?.slides || []).find((item) => item.slideId === slideId);
    if (!slide) return;
    const locked = !slide.locked;
    if (locked && activeSlideIdRef.current === slideId) {
      setEditingTextId(null);
      setCroppingId(null);
    }
    try {
      await flushAll();
      replaceSlide(slideId, { locked });
      await patchSlide(presentationId, slideId, { locked });
    } catch (err: any) {
      await load();
      setError(err?.response?.data?.message || COPY.saveError);
    }
  }, [flushAll, load, presentationId, readOnly]);

  const handleReorderSlides = useCallback(async (sourceId: string, targetId: string) => {
    if (readOnly || specialMode || !presentationId || sourceId === targetId) return;
    await flushAll();
    const currentSlides = deckRef.current?.slides || [];
    const ids = moveSlideId(currentSlides.map((slide) => slide.slideId), sourceId, targetId);
    if (ids.join() === currentSlides.map((slide) => slide.slideId).join()) return;
    setDeck((current) => {
      if (!current?.slides) return current;
      const next = { ...current, slides: applySlideOrders(current.slides, ids) };
      deckRef.current = next;
      return next;
    });
    try {
      await reorderSlides(presentationId, ids);
    } catch (err: any) {
      await load();
      setError(err?.response?.data?.message || COPY.reorderFailed);
    }
  }, [flushAll, load, presentationId, readOnly]);

  const canSaveToLibrary = canManageTemplates && !libraryMode && !templateMode;
  const canAddAsTemplate = canSaveToLibrary;
  const canCopyLayoutToKit = canManageTemplates && templateMode;
  const openKitLayoutDialog = useCallback((
    mode: AddLayoutToKitMode,
    options: { slideId?: string; sourceTemplateId?: string; layoutId?: string; name?: string },
  ) => {
    const excludeId = mode === 'add' ? null : options.sourceTemplateId;
    const kits = kitsForLayoutTarget(templates, deck?.pageFormat, excludeId);
    const preferred = mode === 'add' && deck?.templateId && kits.some((kit) => kit._id === deck.templateId)
      ? deck.templateId
      : (kits[0]?._id || '');
    setKitLayoutDialog({
      mode,
      slideId: options.slideId,
      sourceTemplateId: options.sourceTemplateId,
      layoutId: options.layoutId,
    });
    setKitLayoutName(options.name || '');
    setKitLayoutKitId(preferred);
    setKitLayoutError(null);
  }, [deck?.pageFormat, deck?.templateId, templates]);
  const saveSlideToLibrary = useCallback(async (slideId: string) => {
    if (!canSaveToLibrary || !deck) return;
    const slides = deck.slides || [];
    const index = slides.findIndex((slide) => slide.slideId === slideId);
    const slide = index >= 0 ? slides[index] : null;
    if (!slide) return;
    try {
      await flushAll();
      const latest = (deckRef.current?.slides || []).find((item) => item.slideId === slideId) || slide;
      const created = await createLibrarySlide({
        name: suggestedLibrarySlideName(deck.name, index),
        pageFormat: resolvePageFormat(deck.pageFormat),
        backgroundColor: latest.backgroundColor,
        objects: latest.objects,
      });
      setLibrarySlides((current) => [created, ...current.filter((item) => item._id !== created._id)]);
      setDockTab('slideLibrary');
      setSnack(COPY.savedToLibrary);
    } catch (err: any) {
      setError(err?.response?.data?.message || COPY.saveError);
    }
  }, [canSaveToLibrary, deck, flushAll]);

  const openAddAsTemplate = useCallback((slideId: string) => {
    if (!canAddAsTemplate || !deck) return;
    const slides = deck.slides || [];
    const index = slides.findIndex((slide) => slide.slideId === slideId);
    openKitLayoutDialog('add', {
      slideId,
      name: suggestedLibrarySlideName(deck.name, index >= 0 ? index : 0),
    });
  }, [canAddAsTemplate, deck, openKitLayoutDialog]);

  const openCopyLayoutToKit = useCallback((sourceTemplateId: string, layoutId: string) => {
    if (!canManageTemplates) return;
    const source = templates.find((template) => template._id === sourceTemplateId);
    const layout = source?.layouts.find((item) => item.layoutId === layoutId);
    openKitLayoutDialog('copy', {
      sourceTemplateId,
      layoutId,
      name: layout?.name || COPY.newLayout,
    });
  }, [canManageTemplates, openKitLayoutDialog, templates]);

  const openMoveLayoutToKit = useCallback((sourceTemplateId: string, layoutId: string) => {
    if (!canManageTemplates) return;
    const source = templates.find((template) => template._id === sourceTemplateId);
    const layout = source?.layouts.find((item) => item.layoutId === layoutId);
    openKitLayoutDialog('move', {
      sourceTemplateId,
      layoutId,
      name: layout?.name || COPY.newLayout,
    });
  }, [canManageTemplates, openKitLayoutDialog, templates]);

  const duplicateLayoutInKit = useCallback(async (sourceTemplateId: string, layoutId: string) => {
    await copyTemplateLayout(sourceTemplateId, layoutId, { targetTemplateId: sourceTemplateId });
    await reloadPickerTemplates();
    if (templateMode) await load();
  }, [load, reloadPickerTemplates, templateMode]);

  const submitKitLayoutDialog = useCallback(async (openAfter?: boolean) => {
    if (!kitLayoutDialog || !kitLayoutKitId) return;
    setKitLayoutBusy(true);
    setKitLayoutError(null);
    try {
      await flushAll();
      if (kitLayoutDialog.mode === 'add') {
        const slideId = kitLayoutDialog.slideId;
        const latest = (deckRef.current?.slides || []).find((item) => item.slideId === slideId);
        if (!latest) throw new Error(COPY.saveError);
        const created = await addTemplateLayout(kitLayoutKitId, {
          name: kitLayoutName.trim() || COPY.newLayout,
          objects: latest.objects,
          backgroundColor: latest.backgroundColor,
          sourcePageFormat: resolvePageFormat(deckRef.current?.pageFormat),
        });
        await reloadPickerTemplates();
        setKitLayoutDialog(null);
        if (openAfter && onEditTemplate) {
          onEditTemplate(kitLayoutKitId, created.layoutId, deckRef.current?.name);
        } else {
          setSnack(COPY.addAsTemplateDone);
        }
        return;
      }
      const sourceTemplateId = kitLayoutDialog.sourceTemplateId;
      const layoutId = kitLayoutDialog.layoutId;
      if (!sourceTemplateId || !layoutId) throw new Error(COPY.saveError);
      await copyTemplateLayout(sourceTemplateId, layoutId, {
        targetTemplateId: kitLayoutKitId,
        name: kitLayoutName.trim() || undefined,
        removeSource: kitLayoutDialog.mode === 'move',
      });
      await reloadPickerTemplates();
      if (templateMode) await load();
      setKitLayoutDialog(null);
      setSnack(kitLayoutDialog.mode === 'move' ? COPY.moveLayoutDone : COPY.copyLayoutDone);
    } catch (err: any) {
      setKitLayoutError(err?.response?.data?.message || err?.message || COPY.saveError);
    } finally {
      setKitLayoutBusy(false);
    }
  }, [flushAll, kitLayoutDialog, kitLayoutKitId, kitLayoutName, load, onEditTemplate, reloadPickerTemplates, templateMode]);

  const stripSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const handleStripDragEnd = useCallback((event: DragEndEvent) => {
    const overId = event.over?.id;
    const activeId = String(event.active.id);
    if (!overId) return;
    if (isSlideLibraryDropTarget(overId)) {
      void saveSlideToLibrary(activeId);
      return;
    }
    if (String(overId) !== activeId) {
      void handleReorderSlides(activeId, String(overId));
    }
  }, [handleReorderSlides, saveSlideToLibrary]);

  useEffect(() => {
    const isTypingTarget = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      return target.isContentEditable || Boolean(target.closest('.ProseMirror, [contenteditable="true"]'));
    };
    const onKey = (event: KeyboardEvent) => {
      if (presenting || workspaceMode === 'overview' || importOpen || historyOpen) return;
      const mod = event.ctrlKey || event.metaKey;
      const key = event.key.toLowerCase();
      if (mod && (key === 'z' || key === 'y')) {
        if (isTypingTarget(event.target)) return;
        event.preventDefault();
        if (key === 'y' || event.shiftKey) applyRedo();
        else applyUndo();
        return;
      }
      if (event.key === 'Escape' && isolatedObjectId && activeSlide) {
        event.preventDefault();
        setEditingTextId(null);
        const isolated = activeSlide.objects.find((object) => object.id === isolatedObjectId);
        isolatedObjectIdRef.current = null;
        setIsolatedObjectId(null);
        if (isolated?.groupId) {
          const ids = expandGroupSelection(activeSlide.objects, [isolated.id]);
          selectedObjectIdsRef.current = ids;
          setSelectedObjectIds(ids);
          setDockTab('properties');
        }
        return;
      }
      if (mod && key === 'g') {
        event.preventDefault();
        if (event.shiftKey) ungroupSelected();
        else groupSelected();
        return;
      }
      if (isTypingTarget(event.target)) return;
      if (mod && key === 'x' && selectedObjects.length) {
        if (readOnly) return;
        event.preventDefault();
        clipboardRef.current = JSON.parse(JSON.stringify(selectedObjects)) as SlideObject[];
        removeSelectedObject();
        return;
      }
      if (mod && key === 'c' && selectedObjects.length) {
        event.preventDefault();
        clipboardRef.current = JSON.parse(JSON.stringify(selectedObjects)) as SlideObject[];
        return;
      }
      if (mod && key === 'v') {
        return;
      }
      const nudge = nudgeOffset(event.key, event.shiftKey);
      if (nudge) {
        if (readOnly || !activeSlide || !selectedObjectIds.length || !canMoveSelection(activeSlide.objects, selectedObjectIds)) return;
        event.preventDefault();
        const ids = new Set(selectedObjectIds);
        changeObjects(activeSlide.objects.map((object) => (
          ids.has(object.id)
            ? { ...object, x: object.x + nudge.dx, y: object.y + nudge.dy }
            : object
        )));
        return;
      }
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      if (!selectedObjectId) return;
      event.preventDefault();
      removeSelectedObject();
    };
    const onPaste = (event: Event) => {
      if (!(event instanceof ClipboardEvent)) return;
      if (presenting || workspaceMode !== 'edit' || importOpen || historyOpen || readOnly) return;
      if (isTypingTarget(event.target)) return;
      if (!activeSlide) return;
      const html = event.clipboardData?.getData('text/html') || '';
      const text = event.clipboardData?.getData('text/plain') || '';
      const parsed = parseClipboardTable(html || text);
      const selectedTable = selectedObjects.length === 1 && selectedObjects[0].type === 'table'
        ? selectedObjects[0]
        : null;
      const selectedChart = selectedObjects.length === 1 && selectedObjects[0].type === 'chart'
        ? selectedObjects[0]
        : null;
      if (parsed) {
        event.preventDefault();
        if (selectedChart) {
          if (selectedChart.locks.content && !advancedMode) {
            setError(COPY.tableContentLocked);
            return;
          }
          const result = chartFromGrid(parsed.grid, selectedChart.chart);
          if (result.truncated) setError(COPY.chartPasteTruncated);
          changeObjects(activeSlide.objects.map((object) => (
            object.id === selectedChart.id ? { ...object, chart: result.chart } : object
          )));
          return;
        }
        if (selectedTable?.locks.content && !advancedMode) {
          setError(COPY.tableContentLocked);
          return;
        }
        if (parsed.truncated) setError(COPY.tablePasteTruncated);
        if (selectedTable) {
          changeObjects(activeSlide.objects.map((object) => (
            object.id === selectedTable.id
              ? fitTableObjectToPage({
                ...object,
                table: applyGridToTable(object.table, parsed.grid),
              }, pageSize)
              : object
          )));
          return;
        }
        if (activeTool === 'chart') {
          const result = chartFromGrid(parsed.grid, createDefaultChartObject({
            z: 1,
            kind: settings.chartDefaults.kind,
            colorIds: settings.chartDefaults.seriesColorIds,
            showLegend: settings.chartDefaults.showLegend,
            showValues: settings.chartDefaults.showValues,
            background: settings.chartDefaults.background,
          }).chart);
          if (result.truncated) setError(COPY.chartPasteTruncated);
          const object = createDefaultChartObject({
            z: Math.max(0, ...activeSlide.objects.map((item) => item.z)) + 1,
            kind: settings.chartDefaults.kind,
            colorIds: settings.chartDefaults.seriesColorIds,
            showLegend: settings.chartDefaults.showLegend,
            showValues: settings.chartDefaults.showValues,
            background: settings.chartDefaults.background,
          });
          object.chart = result.chart;
          changeObjects([...activeSlide.objects, object]);
          selectCreatedObject(object.id);
          setActiveTool('chart');
          return;
        }
        const object = createDefaultTableObject({
          z: Math.max(0, ...activeSlide.objects.map((item) => item.z)) + 1,
          cols: settings.tableDefaults.cols,
          rows: settings.tableDefaults.rows,
          fontSize: settings.tableDefaults.fontSize,
          headerBg: hexForSettingsColor(settings, settings.tableDefaults.headerBgColorId),
          grid: hexForSettingsColor(settings, settings.tableDefaults.gridColorId),
        });
        object.table = tableFromGrid(parsed.grid, true);
        changeObjects([...activeSlide.objects, fitTableObjectToPage(object, pageSize)]);
        selectCreatedObject(object.id);
        setActiveTool('table');
        return;
      }
      if (clipboardRef.current?.length) {
        event.preventDefault();
        const copies = cloneSelection(clipboardRef.current, activeSlide.objects);
        clipboardRef.current = copies.map((item) => ({ ...item }));
        changeObjects([...activeSlide.objects, ...copies]);
        selectedObjectIdsRef.current = copies.map((item) => item.id);
        setSelectedObjectIds(selectedObjectIdsRef.current);
        isolatedObjectIdRef.current = null;
        setIsolatedObjectId(null);
        setDockTab('properties');
        return;
      }
      const imageFile = readClipboardImageFile(event.clipboardData);
      if (imageFile) {
        event.preventDefault();
        void placeLocalImageRef.current(imageFile, { failedCopy: COPY.clipboardImageFailed });
        return;
      }
      if (clipboardHasFilePayload(event.clipboardData || {})) {
        event.preventDefault();
        setError(COPY.unsupportedImageType);
        return;
      }
      if (selectedTable) {
        event.preventDefault();
        setError(COPY.tablePasteFailed);
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('paste', onPaste);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('paste', onPaste);
    };
  // Handlers close over the state already listed in the dependency array.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presenting, workspaceMode, importOpen, historyOpen, selectedObjectId, selectedObjectIds, selectedObjects, isolatedObjectId, activeSlide, slides.length, presentationId, activeTool, pageSize]);

  const addObject = (type: 'text' | 'image') => {
    if (!activeSlide || readOnly) return;
    if (type === 'image') {
      setLibraryIntent('insert-image');
      setActiveTool('image');
      setDockTab('library');
      return;
    }
    const object: SlideObject = {
      id: createObjectId(type),
      type,
      x: 160,
      y: 220,
      w: 280,
      h: 72,
      z: Math.max(0, ...activeSlide.objects.map((item) => item.z)) + 1,
      locks: defaultLocks(),
      content: settings.textStyles.newTextPlaceholder || 'NY TEXT',
      trackingMode: 'pin',
      style: {
        ...textStylePatch(textStyleStepsFromSettings(settings.textStyles.steps).find((step) => step.id === settings.textStyles.newTextStyleId)
          || textStyleStepsFromSettings(settings.textStyles.steps)[1]
          || textStyleStepsFromSettings(settings.textStyles.steps)[0]),
        color: hexForSettingsColor(settings, settings.defaultTextColorId),
        padding: DEFAULT_TEXT_PADDING,
        textFit: 'hug',
      },
    };
    changeObjects([...activeSlide.objects, object]);
    selectCreatedObject(object.id, true);
  };

  const addShape = (shape: NonNullable<SlideObjectStyle['shape']>, extras?: Partial<SlideObjectStyle>) => {
    if (!activeSlide || readOnly) return;
    const isLine = shape === 'line';
    const sizes = {
      rect: { w: 420, h: 168, x: 240, y: 380 },
      roundRect: { w: 420, h: 168, x: 240, y: 380 },
      ellipse: { w: 280, h: 280, x: 320, y: 300 },
      triangle: { w: 280, h: 240, x: 320, y: 320 },
      line: { w: 720, h: 28, x: 200, y: 540 },
    }[shape];
    const object: SlideObject = {
      id: createObjectId(isLine ? 'line' : 'shape'),
      type: 'shape',
      ...sizes,
      z: Math.max(0, ...activeSlide.objects.map((item) => item.z)) + 1,
      locks: defaultLocks(),
      style: {
        shape,
        backgroundColor: isLine ? 'transparent' : hexForSettingsColor(settings, settings.defaultShapeColorId),
        strokeColor: hexForSettingsColor(settings, settings.defaultShapeColorId),
        strokeWidth: isLine ? 3 : 0,
        strokeDash: extras?.strokeDash || 'solid',
        ...extras,
      },
    };
    changeObjects([...activeSlide.objects, object]);
    selectCreatedObject(object.id);
  };

  const addTable = () => {
    if (!activeSlide || readOnly) return;
    const object = createDefaultTableObject({
      z: Math.max(0, ...activeSlide.objects.map((item) => item.z)) + 1,
      cols: settings.tableDefaults.cols,
      rows: settings.tableDefaults.rows,
      fontSize: settings.tableDefaults.fontSize,
      headerBg: hexForSettingsColor(settings, settings.tableDefaults.headerBgColorId),
      grid: hexForSettingsColor(settings, settings.tableDefaults.gridColorId),
    });
    changeObjects([...activeSlide.objects, object]);
    selectCreatedObject(object.id);
    setActiveTool('table');
  };

  const addChart = () => {
    if (!activeSlide || readOnly) return;
    const object = createDefaultChartObject({
      z: Math.max(0, ...activeSlide.objects.map((item) => item.z)) + 1,
      kind: settings.chartDefaults.kind,
      colorIds: settings.chartDefaults.seriesColorIds,
      showLegend: settings.chartDefaults.showLegend,
      showValues: settings.chartDefaults.showValues,
      background: settings.chartDefaults.background,
    });
    changeObjects([...activeSlide.objects, object]);
    selectCreatedObject(object.id);
    setActiveTool('chart');
  };

  const changeSelectedChart = (
    action: 'kind' | 'legend' | 'values' | 'add-series' | 'remove-series' | 'add-category' | 'remove-category',
    kind?: ChartKind,
  ) => {
    if (!activeSlide || readOnly || selectedObject?.type !== 'chart') return;
    const styleLocked = selectedObject.locks.style && !advancedMode;
    const contentLocked = selectedObject.locks.content && !advancedMode;
    if ((action === 'kind' || action === 'legend' || action === 'values') && styleLocked) return;
    if ((action === 'add-series' || action === 'remove-series' || action === 'add-category' || action === 'remove-category') && contentLocked) {
      setError(COPY.tableContentLocked);
      return;
    }
    const chart = normalizeChart(selectedObject.chart);
    const next = action === 'kind' && kind
      ? setChartKind(chart, kind)
      : action === 'legend'
        ? { ...chart, showLegend: !chart.showLegend }
        : action === 'values'
          ? { ...chart, showValues: !chart.showValues }
          : action === 'add-series'
            ? resizeChartSeries(chart, chart.series.length + 1)
            : action === 'remove-series'
              ? resizeChartSeries(chart, chart.series.length - 1)
              : action === 'add-category'
                ? resizeChartCategories(chart, chart.categories.length + 1)
                : resizeChartCategories(chart, chart.categories.length - 1);
    changeObjects(activeSlide.objects.map((object) => (
      object.id === selectedObject.id ? { ...object, chart: next } : object
    )));
  };

  const changeSelectedTableGrid = (action: 'add-row' | 'remove-row' | 'add-col' | 'remove-col' | 'toggle-header') => {
    if (!activeSlide || readOnly || selectedObject?.type !== 'table') return;
    if (selectedObject.locks.content && !advancedMode) {
      setError(COPY.tableContentLocked);
      return;
    }
    const table = normalizeTable(selectedObject.table);
    const cols = table.colWidths.length;
    const rows = table.rows.length;
    const next = action === 'toggle-header'
      ? setTableHeaderRow(table, !table.headerRow)
      : action === 'add-row'
        ? resizeTableGrid(table, cols, rows + 1)
        : action === 'remove-row'
          ? resizeTableGrid(table, cols, rows - 1)
          : action === 'add-col'
            ? resizeTableGrid(table, cols + 1, rows)
            : resizeTableGrid(table, cols - 1, rows);
    changeObjects(activeSlide.objects.map((object) => (
      object.id === selectedObject.id ? { ...object, table: next } : object
    )));
  };

  const persistBackground = (color: string) => {
    if (!activeSlide || readOnly || activeSlide.locked || libraryLocked) return;
    replaceSlide(activeSlide.slideId, { backgroundColor: color });
    saveQueue.schedule(activeSlide.slideId, { backgroundColor: color });
  };

  const applyTextStyleStep = (step: TextStyleStep) => {
    if (!activeSlide || !selectedObjectIds.length || readOnly) return;
    const texts = selectedObjects.filter(hasEditableText);
    if (!texts.length) return;
    if (texts.every((object) => object.locks.style) && !advancedMode) return;
    const patch = textStylePatch(step);
    const next = applyTextBoxFitAll(
      applyTextStyleStepToSelection(activeSlide.objects, texts.map((object) => object.id), patch, step.list),
      texts.map((object) => object.id),
      pageSize,
    );
    syncLiveContent(next);
    changeObjects(next);
  };

  const toggleList = (kind: ListKind) => {
    if (!activeSlide || !selectedObjectIds.length || readOnly) return;
    const texts = selectedObjects.filter((object) => hasEditableText(object) && (!object.locks.content || advancedMode));
    if (!texts.length) return;
    const live = textEditorRef.current;
    const editingLive = selectedObject
      && selectedObjectIds.length === 1
      && editingTextId === selectedObject.id
      && isLiveTextEditor(live, selectedObject.id)
      ? live
      : null;
    if (editingLive && applyLiveList(editingLive.editor, kind)) {
      persistInlineHtml(editingLive.editor.getHTML());
      return;
    }
    const ids = new Set(texts.map((object) => object.id));
    changeObjects(activeSlide.objects.map((object) => {
      if (!ids.has(object.id)) return object;
      const content = applyListHtml(object.content || '', kind);
      return applyTextBoxFit({ ...object, content }, content, pageSize);
    }));
  };

  const applyPlacedImage = (
    slideId: string,
    objectId: string,
    imageUrl: string,
    size?: { width?: number; height?: number },
    options?: { preserveFrame?: boolean; assetVersionId?: string | null; fit?: 'contain' | 'cover' }
  ) => {
    const slide = deckRef.current?.slides?.find((item) => item.slideId === slideId);
    if (!slide) return;
    persistObjects(slideId, slide.objects.map((object) => {
      if (object.id !== objectId) return object;
      return applyPlacedMedia(object, {
        imageUrl,
        width: size?.width,
        height: size?.height,
        assetVersionId: options?.assetVersionId || undefined,
      }, { preserveFrame: options?.preserveFrame, fit: options?.fit });
    }));
  };

  const placeAsset = (
    asset: VisibleAsset,
    x = 160,
    y = 200,
    intent: LibraryIntent = 'insert-image',
    options?: { forceInsert?: boolean; catalogKind?: LibraryCatalogKind | null }
  ) => {
    if (!activeSlide || readOnly) return;
    const slideId = activeSlide.slideId;
    const selectedById = activeSlide.objects.find((object) => object.id === selectedObjectId);
    const selected = (selectedById && (selectedById.type === 'image' || selectedById.type === 'product' || isFillableShape(selectedById)))
      ? selectedById
      : (activeSlide.objects.find((object) => selectedObjectIds.includes(object.id) && (
        object.type === 'image' || object.type === 'product' || isFillableShape(object)
      )) || selectedById);
    const action = resolvePlaceAction(selected, intent, options);
    const fillSelected = action.mode === 'fill';
    const containReplace = fillSelected && shouldContainPlacedMedia({
      selected,
      intent,
      asset,
      catalogKind: options?.catalogKind,
    });
    const mediaStyle = { objectFit: 'contain' as const, backgroundColor: 'transparent' };
    const placeType = action.type === 'product' ? 'product' as const : 'image' as const;
    let objectId = createObjectId(placeType);
    const bound = !templateMode && slideHasBindings(activeSlide.objects);
    const droppingProduct = intent === 'insert-product' || placeType === 'product' || Boolean(fillSelected && selected?.type === 'product');
    const populateTarget = bound && droppingProduct
      ? resolvePopulateTarget(activeSlide.objects, selected?.id || selectedObjectId)
      : undefined;

    if (populateTarget) {
      objectId = populateTarget.id;
      const media = {
        assetId: asset._id,
        assetVersionId: asset.currentVersionId || null,
        imageUrl: asset.previewUrl || '',
      };
      changeObjects(populateBoundSlide(activeSlide.objects, populateTarget.id, fieldValuesFromAsset(asset, settingsLogicalFields(settings)), media));
      selectObject(populateTarget.id);
      void getPresentationAsset(asset._id).then((detail) => {
        const slide = deckRef.current?.slides?.find((item) => item.slideId === slideId);
        if (!slide) return;
        persistObjects(slideId, populateBoundSlide(slide.objects, populateTarget.id, fieldValuesFromAsset(detail, settingsLogicalFields(settings)), media));
      }).catch(() => undefined);
      void placePresentationAsset(asset._id, asset.currentVersionId).then((placed) => {
        if (!placed.imageUrl) return;
        applyPlacedImage(slideId, objectId, placed.imageUrl, placed, {
          preserveFrame: true,
          assetVersionId: placed.assetVersionId,
          fit: containReplace ? 'contain' : 'cover',
        });
      }).catch(() => setError(COPY.placeFailed));
      return;
    }

    if (fillSelected && selected && action.type === 'shape') {
      objectId = selected.id;
      changeObjects(activeSlide.objects.map((object) => (
        object.id === selected.id ? filledShapeObject(object, asset) : object
      )));
    } else if (fillSelected && selected) {
      objectId = selected.id;
      changeObjects(activeSlide.objects.map((object) => object.id === selected.id ? {
        ...object,
        type: object.type === 'product' ? 'product' : placeType,
        assetId: asset._id,
        assetVersionId: asset.currentVersionId || null,
        trackingMode: object.trackingMode || 'pin',
        imageUrl: asset.previewUrl || '',
        style: {
          ...object.style,
          ...mediaStyle,
          cropLeft: 0,
          cropTop: 0,
          cropRight: 0,
          cropBottom: 0,
        },
      } : object));
    } else {
      const object: SlideObject = {
        id: objectId,
        type: placeType,
        x: Math.max(40, Math.min(pageSize.width - 640, x)),
        y: Math.max(40, Math.min(pageSize.height - 480, y)),
        w: placeType === 'product' ? 480 : 640,
        h: placeType === 'product' ? 480 : 480,
        z: Math.max(0, ...activeSlide.objects.map((item) => item.z)) + 1,
        locks: defaultLocks(),
        assetId: asset._id,
        assetVersionId: asset.currentVersionId || null,
        trackingMode: 'pin',
        imageUrl: asset.previewUrl || '',
        style: mediaStyle,
      };
      if (placeType === 'product') {
        const product = attachDefaultLinkedFields(object, {
          title: asset.title,
          articleNumber: asset.articleNumber,
        });
        const texts = createProductLinkedTexts(product, {
          title: asset.title,
          articleNumber: asset.articleNumber,
        }, {
          heading: settings.textStyles.importHeading,
          body: settings.textStyles.importBody,
          color: hexForSettingsColor(settings, settings.defaultTextColorId),
        });
        const groupId = createObjectId('grp');
        changeObjects([...activeSlide.objects, ...[product, ...texts].map((item) => ({ ...item, groupId }))]);
        selectObject(product.id);
      } else {
        changeObjects([...activeSlide.objects, object]);
        selectCreatedObject(object.id);
      }
    }

    void placePresentationAsset(asset._id, asset.currentVersionId).then((placed) => {
      if (!placed.imageUrl) return;
      const applyWhenReady = (attempt = 0) => {
        const slide = deckRef.current?.slides?.find((item) => item.slideId === slideId);
        if (slide?.objects.some((object) => object.id === objectId)) {
          applyPlacedImage(slideId, objectId, placed.imageUrl, placed, {
            preserveFrame: fillSelected,
            assetVersionId: placed.assetVersionId,
            fit: containReplace ? 'contain' : 'cover',
          });
          return;
        }
        if (attempt < 10) window.setTimeout(() => applyWhenReady(attempt + 1), 40);
      };
      applyWhenReady();
    }).catch(() => setError(COPY.placeFailed));
  };

  const placeLocalImage = async (file: File, options?: {
    x?: number;
    y?: number;
    forceInsert?: boolean;
    failedCopy?: string;
  }) => {
    if (!activeSlide || readOnly) return;
    const slide = activeSlide;
    const frameW = 640;
    const frameH = 480;
    try {
      const placed = await uploadLocalPresentationImage(file);
      if (!placed.imageUrl) throw new Error('missing url');
      const selectedById = slide.objects.find((object) => object.id === selectedObjectId);
      const selected = (selectedById && (selectedById.type === 'image' || selectedById.type === 'product' || isFillableShape(selectedById)))
        ? selectedById
        : (slide.objects.find((object) => selectedObjectIds.includes(object.id) && (
          object.type === 'image' || object.type === 'product' || isFillableShape(object)
        )) || selectedById);
      const action = resolvePlaceAction(selected, 'insert-image', { forceInsert: options?.forceInsert });
      if (action.mode === 'fill' && selected && action.type === 'shape') {
        changeObjects(slide.objects.map((object) => (
          object.id === selected.id ? filledShapeFromUrl(object, placed.imageUrl) : object
        )));
        selectObject(selected.id);
        return;
      }
      if (action.mode === 'fill' && selected) {
        changeObjects(slide.objects.map((object) => (
          object.id === selected.id ? { ...filledMediaFromUrl(object, placed), type: object.type === 'product' ? 'product' : 'image' } : object
        )));
        selectObject(selected.id);
        return;
      }
      const object: SlideObject = {
        id: createObjectId('image'),
        type: 'image',
        x: Math.max(40, Math.min(pageSize.width - frameW, options?.x ?? 160)),
        y: Math.max(40, Math.min(pageSize.height - frameH, options?.y ?? 200)),
        w: frameW,
        h: frameH,
        z: Math.max(0, ...slide.objects.map((item) => item.z)) + 1,
        locks: defaultLocks(),
        assetId: null,
        assetVersionId: null,
        trackingMode: 'pin',
        imageUrl: placed.imageUrl,
        style: { objectFit: 'contain', backgroundColor: 'transparent' },
      };
      changeObjects([...slide.objects, object]);
      selectCreatedObject(object.id);
    } catch (err: any) {
      setError(err?.response?.data?.message || options?.failedCopy || COPY.insertFromFileFailed);
    }
  };
  placeLocalImageRef.current = placeLocalImage;

  const placeFieldText = (payload: LibraryFieldTransfer, x: number, y: number) => {
    if (!activeSlide || readOnly) return;
    const object = createLibraryTextObject(payload, {
      x,
      y,
      z: Math.max(0, ...activeSlide.objects.map((item) => item.z)) + 1,
    }, pageSize);
    changeObjects([...activeSlide.objects, object]);
    selectCreatedObject(object.id, true);
  };

  const runHealth = async () => {
    if (!presentationId) return;
    await flushAll();
    const result = await runHealthCheck(presentationId);
    setHealth(result);
    setHealthOpen(true);
  };

  const switchLanguageVersion = useCallback(async (id: string) => {
    if (!id || id === presentationId || !onOpenPresentation) return;
    try {
      await flushAll();
      onOpenPresentation(id);
    } catch (err: any) {
      setError(err?.response?.data?.message || COPY.saveError);
    }
  }, [flushAll, onOpenPresentation, presentationId]);

  const submitLanguageVersion = useCallback(async (input: { language: PresentationLanguage; market: PresentationMarket }) => {
    if (!presentationId) return;
    setLanguageVersionBusy(true);
    setLanguageVersionError(null);
    try {
      await flushAll();
      const created = await createLanguageVersion(presentationId, input);
      setLanguageVersionOpen(false);
      if (onOpenPresentation) onOpenPresentation(created._id);
      else await load();
    } catch (err: any) {
      setLanguageVersionError(err?.response?.data?.message || COPY.languageVersionFailed);
    } finally {
      setLanguageVersionBusy(false);
    }
  }, [flushAll, load, onOpenPresentation, presentationId]);

  const leaveCurrentLanguageGroup = useCallback(async () => {
    if (!presentationId) return;
    try {
      await flushAll();
      await leaveLanguageGroup(presentationId);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || COPY.leaveLanguageGroupFailed);
    }
  }, [flushAll, load, presentationId]);

  const revealOriginReview = (slideId: string, objectId: string) => {
    const item = (deck?.originReview?.items || []).find((entry) => entry.slideId === slideId && entry.objectId === objectId);
    const resolved = item || { slideId, objectId, kind: objectId === slideId ? 'page' as const : 'text' as const, pageIndex: 0, signature: '' };
    setWorkspaceMode('edit');
    setOriginReviewItem(resolved);
    if (resolved.kind === 'page') return;
    pendingSelectObjectIdRef.current = objectId;
    if (slideId !== activeSlideIdRef.current) {
      applySlideSelection({
        selectedIds: [slideId],
        activeId: slideId,
        rangeAnchorId: slideId,
      });
    } else {
      selectObject(objectId, { isolate: true });
    }
  };

  const applyOriginReviewCopy = async (item: OriginReviewItem) => {
    if (!presentationId || readOnly) return;
    setOriginReviewBusy(true);
    try {
      await flushAll();
      if (item.kind === 'page') {
        const result = await insertOriginReviewPage(presentationId, { slideId: item.slideId });
        setDeck((current) => (current ? {
          ...current,
          originReview: result.originReview,
          slides: [...(current.slides || []).filter((slide) => slide.slideId !== result.slide.slideId), result.slide]
            .sort((left, right) => (left.order ?? 0) - (right.order ?? 0)),
        } : current));
        applySlideSelection({
          selectedIds: [result.slide.slideId],
          activeId: result.slide.slideId,
          rangeAnchorId: result.slide.slideId,
        });
        const remaining = result.originReview?.items || [];
        setOriginReviewItem(remaining[0] || null);
        return;
      }
      const result = await copyOriginReview(presentationId, { slideId: item.slideId, objectId: item.objectId });
      setDeck((current) => (current ? {
        ...current,
        originReview: result.originReview,
        slides: (current.slides || []).map((slide) => (
          slide.slideId === result.slide.slideId ? { ...slide, objects: result.slide.objects } : slide
        )),
      } : current));
      const remaining = result.originReview?.items || [];
      setOriginReviewItem(remaining[0] || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || (item.kind === 'page' ? COPY.originReviewInsertFailed : COPY.originReviewFailed));
    } finally {
      setOriginReviewBusy(false);
    }
  };

  const applyOriginReviewDismiss = async (item: OriginReviewItem) => {
    if (!presentationId || readOnly) return;
    setOriginReviewBusy(true);
    try {
      const result = await dismissOriginReview(presentationId, {
        slideId: item.slideId,
        objectId: item.objectId,
        signature: item.signature,
      });
      setDeck((current) => (current ? { ...current, originReview: result.originReview } : current));
      const remaining = result.originReview?.items || [];
      setOriginReviewItem(remaining[0] || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || COPY.originReviewDismissFailed);
    } finally {
      setOriginReviewBusy(false);
    }
  };

  const openOriginPresentation = async (item: OriginReviewItem) => {
    const originalId = deck?.originReview?.originalId;
    if (!originalId || !onOpenPresentation) return;
    try {
      await flushAll();
      onOpenPresentation(originalId, {
        focusSlideId: item.slideId,
        focusObjectId: item.kind === 'page' ? undefined : item.objectId,
      });
    } catch (err: any) {
      setError(err?.response?.data?.message || COPY.saveError);
    }
  };

  const changeStatus = async (status: PresentationStatus) => {
    if (!presentationId) return;
    try {
      await flushAll();
      const next = await transitionPresentationStatus(presentationId, status);
      setDeck(next);
      setMediaUpdates(next.mediaUpdates || { applied: [], available: [] });
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setHealth({
          readyBlocked: true,
          issues: err.response.data?.issues || [],
          aiEnabled: health.aiEnabled,
        });
        setHealthOpen(true);
        return;
      }
      setError(err?.response?.data?.message || COPY.statusChangeFailed);
    }
  };

  const acceptAvailableMedia = async (items: Array<{ slideId: string; objectId: string }>) => {
    if (!presentationId) return;
    const next = await acceptMediaUpdates(presentationId, items);
    setDeck(next);
    setMediaUpdates(next.mediaUpdates || { applied: [], available: [] });
  };

  const writeObjectHtml = (slideId: string, objectId: string, html: string) => {
    const slide = deckRef.current?.slides?.find((item) => item.slideId === slideId);
    if (!slide) return false;
    const next = slide.objects.map((object) => (
      object.id === objectId ? { ...object, content: html } : object
    ));
    const live = textEditorRef.current;
    if (isLiveTextEditor(live, objectId)) {
      live.editor.commands.setContent(html, false);
      live.markClean?.();
    }
    if (editingTextId === objectId) setEditingTextId(null);
    if (slideId === activeSlideIdRef.current) beginHistory();
    persistObjects(slideId, next, true);
    return true;
  };

  const sendAiChat = async (message: string) => {
    if (!presentationId) return false;
    setAiBusy(true);
    setAiChatMessages((current) => [...current, { role: 'user', content: message }]);
    try {
      const result = await runPresentationChat({
        presentationId,
        message,
        history: aiChatMessages,
      });
      setAiChatMessages((current) => [...current, { role: 'assistant', content: result.text }]);
      return true;
    } catch (err: any) {
      const message = err?.response?.data?.message || COPY.aiUnavailable;
      setError(message);
      setAiChatMessages((current) => [...current, { role: 'assistant', content: message }]);
      return true;
    } finally {
      setAiBusy(false);
    }
  };

  const insertAiChat = (content: string) => {
    const html = htmlFromAiInsert(content);
    if (!activeSlide || !selectedObjectId || !html) {
      setError(COPY.aiNeedsTextSelection);
      return false;
    }
    const selected = activeSlide.objects.find((item) => item.id === selectedObjectId);
    if (!selected || !hasEditableText(selected)) {
      setError(COPY.aiNeedsTextSelection);
      return false;
    }
    if (shouldPromptBoundField(selected, activeSlide.objects)) {
      setBoundFieldPrompt({ kind: 'insert-ai', objectId: selectedObjectId, slideId: activeSlide.slideId, html });
      return false;
    }
    writeObjectHtml(activeSlide.slideId, selectedObjectId, html);
    setSnack(COPY.aiChatInserted);
    return true;
  };

  const applyAiToObject = async (operation: 'rewrite' | 'translate' | 'checkGrammar', instruction?: string, objectId?: string, slideId?: string, targetLanguageOverride?: PresentationLanguage) => {
    const targetSlideId = slideId || activeSlide?.slideId;
    const targetObjectId = objectId || selectedObjectId;
    if (!targetSlideId || !targetObjectId || readOnly || !presentationId) {
      setError(COPY.aiNeedsTextSelection);
      return false;
    }
    if (isLiveTextEditor(textEditorRef.current, targetObjectId)) {
      commitLiveText();
      await saveQueue.flushAll();
    }
    const targetObject = (deckRef.current?.slides || [])
      .find((item) => item.slideId === targetSlideId)
      ?.objects.find((item) => item.id === targetObjectId);
    if (!targetObject || !hasEditableText(targetObject)) {
      setError(COPY.aiNeedsTextSelection);
      return false;
    }
    const targetSlide = (deckRef.current?.slides || []).find((item) => item.slideId === targetSlideId);
    if (shouldPromptBoundField(targetObject, targetSlide?.objects)) {
      setBoundFieldPrompt({
        kind: 'ai',
        objectId: targetObjectId,
        slideId: targetSlideId,
        operation,
        instruction,
      });
      return false;
    }
    setAiBusy(true);
    setSnack(operation === 'translate' ? COPY.aiTranslateBusy : operation === 'checkGrammar' ? COPY.aiGrammarBusy : COPY.aiRewriteBusy);
    try {
      const result = await runPresentationAi({
        presentationId,
        slideId: targetSlideId,
        objectId: targetObjectId,
        operation,
        instruction,
        targetLanguage: operation === 'translate' ? (targetLanguageOverride || translateLanguage) : undefined,
      });
      const slide = deckRef.current?.slides?.find((item) => item.slideId === targetSlideId);
      if (!slide || !result.text) {
        setError(COPY.aiNoResult);
        setSnack(null);
        return false;
      }
      if (stripPresentationText(result.text) === stripPresentationText(targetObject.content)) {
        setSnack(COPY.aiNoChange);
        return false;
      }
      writeObjectHtml(targetSlideId, targetObjectId, result.text);
      setSnack(operation === 'translate' ? COPY.aiTranslateDone : operation === 'checkGrammar' ? COPY.aiGrammarDone : COPY.aiRewriteDone);
      return true;
    } catch (err: any) {
      setError(err?.response?.data?.message || COPY.aiUnavailable);
      setSnack(null);
      return false;
    } finally {
      setAiBusy(false);
    }
  };

  const confirmBoundFieldPrompt = () => {
    const prompt = boundFieldPrompt;
    if (!prompt) return;
    setBoundFieldPrompt(null);
    const changedHtml = prompt.kind === 'edit' ? prompt.html : undefined;
    if (!applyBoundFieldLocal(prompt.objectId, prompt.slideId, prompt.kind === 'edit', changedHtml)) return;
    if (prompt.kind === 'edit') {
      if (changedHtml !== undefined && isLiveTextEditor(textEditorRef.current, prompt.objectId)) {
        textEditorRef.current.editor.commands.setContent(changedHtml, false);
        textEditorRef.current.markClean?.();
        window.requestAnimationFrame(() => textEditorRef.current?.editor.commands.focus('end'));
      }
      setEditingTextId(prompt.objectId);
      return;
    }
    if (prompt.kind === 'ai' && prompt.operation) {
      void applyAiToObject(prompt.operation, prompt.instruction, prompt.objectId, prompt.slideId);
      return;
    }
    if (prompt.kind === 'insert-ai' && prompt.html && prompt.slideId) {
      writeObjectHtml(prompt.slideId, prompt.objectId, prompt.html);
      setSnack(COPY.aiChatInserted);
    }
  };

  const exportPdf = async (slideIds?: string[]) => {
    if (!presentationId) return;
    const all = deckRef.current?.slides || [];
    const wanted = new Set(slideIds || []);
    const slidesToExport = wanted.size
      ? all.filter((slide) => wanted.has(slide.slideId)).sort((a, b) => a.order - b.order)
      : visibleSlides(all);
    if (!slidesToExport.length) {
      setError('Välj minst en slide att exportera.');
      return;
    }
    setPdfBusy(true);
    setSnack('Exporterar PDF…');
    const finishExport = async (result: { publicUrl: string }, fallback: boolean) => {
      if (pdfObjectUrl) URL.revokeObjectURL(pdfObjectUrl);
      const objectUrl = await openExportedPdf(result.publicUrl, pdfDownloadName(deckRef.current?.name));
      setPdfObjectUrl(objectUrl);
      setPdfDialogOpen(false);
      setSnack(fallback
        ? COPY.exportPdfFallback
        : 'PDF klar — den laddas ner till din dator. Klicka Öppna om du inte ser den.');
    };
    const capturePages = async () => {
      const pages: string[] = [];
      for (let index = 0; index < slidesToExport.length; index += 1) {
        const slide = slidesToExport[index];
        setSnack(`Exporterar PDF… slide ${index + 1} av ${slidesToExport.length}`);
        flushSync(() => setPrintSlide(slide));
        const node = printNodeRef.current;
        if (!node) throw new Error('Kunde inte rita sliden för PDF.');
        pages.push(await captureSlideNode(node, slide.backgroundColor || '#FFFFFF', deckRef.current?.pageFormat));
      }
      flushSync(() => setPrintSlide(null));
      return pages;
    };
    try {
      const result = await exportPresentationPdf(presentationId, {
        slideIds: wanted.size ? slidesToExport.map((slide) => slide.slideId) : undefined,
      });
      await finishExport(result, false);
    } catch {
      try {
        const pages = await capturePages();
        const result = await exportPresentationPdf(presentationId, { pages });
        await finishExport(result, true);
      } catch (err: any) {
        flushSync(() => setPrintSlide(null));
        setSnack(null);
        setError(err?.response?.data?.message || err?.message || 'PDF-export misslyckades.');
      }
    } finally {
      setPdfBusy(false);
    }
  };

  if (!deck || !activeSlide) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {error || COPY.opening}
      </Box>
    );
  }

  const presentableSlides = visibleSlides(slides);
  const slideLocked = Boolean(activeSlide.locked);

  const toolButtonSx = { // Koncept A chrome
    textTransform: 'none' as const,
    color: PRES.textPrimary,
    bgcolor: PRES.white,
    border: `1px solid ${PRES.borderSubtle}`,
    borderRadius: 1.5,
    px: 1.25,
    minHeight: 32,
    '&:hover': { borderColor: PRES.borderStrong, bgcolor: PRES.surfaceSunken },
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: PRES.cream }}>
      {templateMode && <TemplateModeBanner />}
      {libraryMode && <LibraryModeBanner />}
      {!libraryMode && (libraryLocked || sourceMissing) && (
        <LibraryLinkedBanner
          sourceMissing={sourceMissing}
          canDetach={canEdit && !readOnly}
          busy={libraryBusy}
          onDetach={() => { void handleDetachLibrary(); }}
        />
      )}
      {!specialMode && isProductLinkedSlide(activeSlide) && <ProductLinkedBanner />}
      <Box sx={{ height: 52, display: 'flex', alignItems: 'center', gap: 1, px: 1.5, bgcolor: PRES.white, borderBottom: `1px solid ${PRES.line}`, flexShrink: 0 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={async () => { await flushAll(); onBack(); }}
          sx={{
            ...toolButtonSx,
            mr: 0.5,
            pr: 1.25,
            color: PRES.interactive,
            fontWeight: 650,
            maxWidth: 280,
            justifyContent: 'flex-start',
            '& .MuiButton-startIcon': { mr: 0.5 },
            '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 20 },
          }}
        >
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {onBackLabel || COPY.allPresentations}
          </Box>
        </Button>
        {canManageSettings && onOpenSettings && <SettingsGearButton onClick={onOpenSettings} />}
        <Box sx={{ width: '1px', alignSelf: 'stretch', my: 1.25, bgcolor: PRES.borderStrong, flexShrink: 0 }} />
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0, ml: 0.5 }}>
          <InputBase
            value={deck.name}
            disabled={readOnly}
            onChange={(event) => setDeck({ ...deck, name: event.target.value })}
            onBlur={() => {
              if (readOnly) return;
              if (libraryMode && libraryEdit?.librarySlideId) void patchLibrarySlide(libraryEdit.librarySlideId, { name: deck.name });
              else if (templateMode && templateEdit?.templateId) void patchTemplate(templateEdit.templateId, { name: deck.name });
              else if (presentationId) void patchPresentation(presentationId, { name: deck.name });
            }}
            sx={{ minWidth: 120, maxWidth: 280, fontWeight: 600, fontSize: 16, color: PRES.textPrimary }}
          />
          <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.body, color: PRES.textSecondary, flexShrink: 0 }}>
            {libraryMode ? COPY.currentViewKindLibrary : templateMode ? COPY.currentViewKindTemplate : COPY.currentViewKindPresentation}
          </Typography>
          {(templateMode || libraryMode) && !readOnly ? (
            <Select
              size="small"
              value={resolvePageFormat(deck.pageFormat)}
              onChange={(event) => {
                const nextFormat = event.target.value as PageFormat;
                if (libraryMode && libraryEdit?.librarySlideId) {
                  void patchLibrarySlide(libraryEdit.librarySlideId, { pageFormat: nextFormat }).then((updated) => {
                    setDeck((current) => (current ? { ...current, pageFormat: updated.pageFormat } : current));
                    setLibrarySlides((current) => current.map((item) => (
                      item._id === updated._id ? { ...item, pageFormat: updated.pageFormat } : item
                    )));
                  }).catch((err: any) => {
                    setError(err?.response?.data?.message || COPY.saveError);
                  });
                  return;
                }
                if (!templateEdit?.templateId) return;
                void patchTemplate(templateEdit.templateId, { pageFormat: nextFormat }).then((updated) => {
                  setDeck((current) => (current ? { ...current, pageFormat: updated.pageFormat } : current));
                  setTemplates((current) => current.map((template) => (
                    template._id === updated._id ? { ...template, pageFormat: updated.pageFormat } : template
                  )));
                }).catch((err: any) => {
                  setError(err?.response?.data?.message || COPY.saveError);
                });
              }}
              sx={{ ml: 1, minWidth: 88, height: 32, fontSize: 13 }}
              title={COPY.pageFormatHelp}
            >
              <MenuItem value="widescreen_16_9">{COPY.pageFormatWidescreen}</MenuItem>
              <MenuItem value="a4_portrait">{COPY.pageFormatA4}</MenuItem>
            </Select>
          ) : (
            <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textTertiary, flexShrink: 0 }}>
              {pageFormatLabel(deck.pageFormat)}
            </Typography>
          )}
        </Box>
        {!specialMode && (
          <StatusMenu
            status={deck.status}
            permissions={userPermissions}
            disabled={false}
            onChange={(status) => void changeStatus(status)}
          />
        )}
        {!specialMode && (
          <LanguageVersionBar
            deck={deck}
            canCreate={canCreate}
            canEdit={canEdit}
            onSwitch={(id) => { void switchLanguageVersion(id); }}
            onCreate={() => {
              setLanguageVersionError(null);
              setLanguageVersionOpen(true);
            }}
            onLeave={() => { void leaveCurrentLanguageGroup(); }}
          />
        )}
        {(readOnly || libraryLocked) && (
          <Typography variant="caption" sx={{ color: PRES.muted }}>
            {libraryLocked ? COPY.libraryLinkedBanner : COPY.readOnly}
          </Typography>
        )}
        {slideLocked && <Typography variant="caption" sx={{ color: PRES.muted }}>{COPY.slideLocked}</Typography>}
        <Typography variant="caption" sx={{ color: saveState === 'error' ? PRES.danger : saveState === 'saved' ? '#2e7d32' : PRES.textTertiary }}>
          {saveState === 'saving' ? COPY.saving : saveState === 'unsaved' ? COPY.unsaved : saveState === 'error' ? COPY.saveError : COPY.saved}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title={COPY.undo}>
          <span>
            <IconButton size="small" onClick={() => void applyUndo()} disabled={readOnly || historyTick < 0 || !canUndo(historyRef.current, activeSlide.slideId)}>
              <UndoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={COPY.redo}>
          <span>
            <IconButton size="small" onClick={() => void applyRedo()} disabled={readOnly || !canRedo(historyRef.current, activeSlide.slideId)}>
              <RedoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={COPY.smartGuidesHint}>
          <Button
            size="small"
            onClick={() => setShowSmartGuides(!showSmartGuides)}
            sx={{ textTransform: 'none', bgcolor: showSmartGuides ? PRES.navy : PRES.white, color: showSmartGuides ? PRES.white : PRES.navy, border: `1px solid ${PRES.line}`, minHeight: 32 }}
          >
            {COPY.smartGuides}
          </Button>
        </Tooltip>
        {(advancedMode || specialMode) && (
          <>
            <FormLikeSnap showGrid={showGrid} snap={snap} setShowGrid={setShowGrid} setSnap={setSnap} />
            {!specialMode && (
              <Select
                size="small"
                value={activeSlide.layoutId}
                onChange={(event) => {
                  const layout = currentTemplate?.layouts.find((item) => item.layoutId === event.target.value);
                  if (!layout || readOnly || slideLocked) return;
                  setLayoutConfirm(layout.layoutId);
                }}
                sx={{ minWidth: 140, height: 32 }}
              >
                {(currentTemplate?.layouts || []).map((layout) => (
                  <MenuItem key={layout.layoutId} value={layout.layoutId}>{layout.name}</MenuItem>
                ))}
              </Select>
            )}
            {templateMode && (
              <Button size="small" onClick={() => { setRenameLayoutValue(currentTemplate?.layouts.find((item) => item.layoutId === activeSlide.slideId)?.name || ''); setRenameLayoutOpen(true); }} sx={{ ...toolButtonSx }}>
                {COPY.renameLayout}
              </Button>
            )}
          </>
        )}
        {!specialMode && (
          <Button
            size="small"
            onClick={() => void runHealth()}
            startIcon={<ReportProblemOutlinedIcon sx={{ fontSize: 16, color: health.readyBlocked ? PRES.danger : PRES.textTertiary }} />}
            sx={{ ...toolButtonSx, color: PRES.textPrimary }}
          >
            {health.issues.length ? `${health.issues.length} ${COPY.healthIssues}` : COPY.health}
          </Button>
        )}
        <IconButton size="small" onClick={(event) => setMoreAnchor(event.currentTarget)} sx={{ color: PRES.textPrimary }}>
          <MoreVertIcon />
        </IconButton>
        {!specialMode && (
          <Button
            variant="contained"
            startIcon={<SlideshowIcon />}
            onClick={() => {
              if (!presentableSlides.length) {
                setSnack(COPY.allSlidesHidden);
                return;
              }
              setPresenting(true);
            }}
            sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none', '&:hover': { bgcolor: PRES.interactiveHover } }}
          >
            {COPY.present}
          </Button>
        )}
        <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
          {templateMode && (
            <MenuItem disabled>
              {COPY.templateModeHelp}
            </MenuItem>
          )}
          {!specialMode && !readOnly && canEdit && (
            <MenuItem onClick={() => {
              setMoreAnchor(null);
              setChangeTemplateId(deck.templateId || '');
              setChangeTemplateOpen(true);
            }}>
              {COPY.changeTemplate}
            </MenuItem>
          )}
          {!specialMode && !readOnly && canEdit && !libraryLocked && (
            <MenuItem onClick={async () => {
              setMoreAnchor(null);
              if (!presentationId || !activeSlide) return;
              try {
                await flushAll();
                const slide = await applySlideLayout(presentationId, activeSlide.slideId);
                replaceSlide(activeSlide.slideId, { objects: slide.objects, layoutId: slide.layoutId });
                setSnack(COPY.updateFromTemplateDone);
              } catch (err: any) {
                setError(err?.response?.data?.message || COPY.updateFromTemplateMissing);
              }
            }}>
              {COPY.updateFromTemplate}
            </MenuItem>
          )}
          {canManageTemplates && !templateMode && onOpenTemplates && (
            <MenuItem onClick={() => { setMoreAnchor(null); onOpenTemplates(); }}>
              {COPY.editTemplates}
            </MenuItem>
          )}
          {canManageTemplates && !libraryMode && onOpenLibrarySlides && (
            <MenuItem onClick={() => { setMoreAnchor(null); onOpenLibrarySlides(); }}>
              {COPY.slideLibrary}
            </MenuItem>
          )}
          {!specialMode && !readOnly && (
            <MenuItem
              disabled={isA4}
              title={isA4 ? COPY.pptxA4Blocked : undefined}
              onClick={() => {
                if (isA4) return;
                setMoreAnchor(null);
                setImportKind('pptx');
                setImportOpen(true);
              }}
            >
              <UploadFileIcon fontSize="small" sx={{ mr: 1 }} /> {COPY.importPptx}
            </MenuItem>
          )}
          {!specialMode && !readOnly && (
            <MenuItem
              onClick={() => {
                setMoreAnchor(null);
                setImportKind('pdf');
                setImportOpen(true);
              }}
            >
              <PictureAsPdfIcon fontSize="small" sx={{ mr: 1 }} /> {COPY.importPdf}
            </MenuItem>
          )}
          {!specialMode && canExport && (
            <MenuItem onClick={() => { setMoreAnchor(null); setPdfDialogOpen(true); }}>
              <PictureAsPdfIcon fontSize="small" sx={{ mr: 1 }} /> {COPY.exportPdf}
            </MenuItem>
          )}
        </Menu>
      </Box>

      <DndContext sensors={stripSensors} collisionDetection={slideLibraryCollision} onDragEnd={handleStripDragEnd}>
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <SlideStrip
          slides={slides}
          activeSlideId={activeSlide.slideId}
          pageFormat={deck.pageFormat}
          readOnly={readOnly || libraryMode}
          onSelect={handleSelectSlide}
          selectedSlideIds={selectedSlideIds.length ? selectedSlideIds : [activeSlide.slideId]}
          onAdd={handleAddSlide}
          onDelete={handleDeleteSlide}
          onDuplicate={handleDuplicateSlide}
          onToggleHidden={handleToggleHidden}
          onToggleLock={handleToggleLock}
          canSaveToLibrary={canSaveToLibrary}
          onSaveToLibrary={(slideId) => { void saveSlideToLibrary(slideId); }}
          canAddAsTemplate={canAddAsTemplate}
          onAddAsTemplate={openAddAsTemplate}
          canCopyLayoutToKit={canCopyLayoutToKit}
          onCopyLayoutToKit={templateEdit?.templateId
            ? (slideId) => openCopyLayoutToKit(templateEdit.templateId, slideId)
            : undefined}
          onMoveLayoutToKit={templateEdit?.templateId && slides.length > 1
            ? (slideId) => openMoveLayoutToKit(templateEdit.templateId, slideId)
            : undefined}
          showPageKindMarks={!specialMode}
        />
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {!specialMode && (
          <MediaUpdatesBanner
            updates={mediaUpdates}
            onAccept={(items) => void acceptAvailableMedia(items)}
            onDismissAvailable={() => setMediaUpdates((current) => ({ ...current, available: [] }))}
            onReveal={(item: MediaUpdateItem) => {
              setWorkspaceMode('edit');
              pendingSelectObjectIdRef.current = item.objectId;
              if (item.slideId !== activeSlideIdRef.current) {
                applySlideSelection({
                  selectedIds: [item.slideId],
                  activeId: item.slideId,
                  rangeAnchorId: item.slideId,
                });
              } else {
                selectObject(item.objectId, { isolate: true });
              }
            }}
          />
          )}
          {!specialMode && deck.generatedByAi ? (
            <GenerateAiBanner
              busy={regenerateBusy}
              onRegenerate={() => {
                if (!deck.generationRunId || regenerateBusy || readOnly) return;
                if (!window.confirm(COPY.aiGeneratedRegenerateConfirm)) return;
                void (async () => {
                  regenerateAbortRef.current?.abort();
                  const controller = new AbortController();
                  regenerateAbortRef.current = controller;
                  setRegenerateBusy(true);
                  try {
                    const run = await regenerateGeneration(deck.generationRunId as string);
                    const finished = await pollGeneration(run._id, () => undefined, controller.signal);
                    if (finished.status === 'completed') await load();
                    else if (finished.status === 'needs_clarification') {
                      setError(finished.clarification?.message || COPY.createWithAiClarify);
                    } else if (finished.status === 'cancelled') {
                      setError(COPY.createWithAiCancelled);
                    } else {
                      setError(finished.errorMessage || COPY.aiGeneratedRegenerateFailed);
                    }
                  } catch (err: any) {
                    if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
                    setError(err?.response?.data?.message || COPY.aiGeneratedRegenerateFailed);
                  } finally {
                    setRegenerateBusy(false);
                  }
                })();
              }}
              onDelete={() => {
                if (!presentationId || !window.confirm(COPY.aiGeneratedDeleteConfirm)) return;
                void deletePresentation(presentationId).then(() => onBack()).catch((err: any) => {
                  setError(err?.response?.data?.message || COPY.deleteFailed);
                });
              }}
            />
          ) : null}
          {!specialMode && deck.originReview?.items?.length ? (
            <OriginReviewBanner
              review={deck.originReview}
              onReveal={(slideId, objectId) => revealOriginReview(slideId, objectId)}
            />
          ) : null}
          {workspaceMode === 'edit' && (
          <EditorToolbar
            activeTool={activeTool}
            onToolChange={(tool) => {
              setActiveTool(tool);
              if (tool === 'image') {
                setLibraryIntent('insert-image');
                setDockTab('library');
              }
              if (tool === 'icon') {
                setLibraryIntent('insert-icon');
                setDockTab('library');
              }
            }}
            showAi={!specialMode && isPresentationsAiEnabled(health.aiEnabled)}
            aiBusy={aiBusy}
            showSmartLayout={!specialMode && advancedMode && canManageTemplates}
            canFitSlot={Boolean(activeSlide && selectedObjectId && canFitObjectInSlot(activeSlide.objects, selectedObjectId))}
            canAdapt={Boolean(activeSlide && wouldAdaptUnlockedStyles(activeSlide.objects, allowedSettingsHexes(settings)))}
            onAiRewrite={() => void applyAiToObject('rewrite')}
            onAiGrammar={() => void applyAiToObject('checkGrammar')}
            onAiTranslate={(language) => void applyAiToObject('translate', undefined, undefined, undefined, language)}
            translateLanguage={translateLanguage}
            translateLanguages={visiblePresentationLanguages(settings).map((id) => ({
              id,
              label: languageLabel(id, settingsLanguageLabel(settings, id)),
            }))}
            onTranslateLanguageChange={setTranslateLanguage}
            onFitSlot={() => {
              if (!activeSlide || !selectedObjectId) return;
              changeObjects(fitObjectInSlot(activeSlide.objects, selectedObjectId));
            }}
            onAdapt={() => {
              if (!activeSlide) return;
              changeObjects(adaptUnlockedStyles(activeSlide.objects, allowedSettingsHexes(settings)));
            }}
            libraryActive={dockTab === 'library'}
            onOpenLibrary={() => { setLibraryIntent('insert-image'); openLibrary(); }}
            onInsertFromFile={canvasReadOnly ? undefined : (file) => { void placeLocalImage(file); }}
            selectedObject={(activeTool === 'image' || activeTool === 'icon')
              ? (croppableSelected || selectedObject)
              : selectedObject}
            styleLocked={Boolean((croppableSelected || selectedObject)?.locks.style && !advancedMode)}
            contentLocked={Boolean((croppableSelected || selectedObject)?.locks.content && !advancedMode)}
            onAddText={() => addObject('text')}
            onAddTable={addTable}
            onAddChart={addChart}
            onTableGrid={changeSelectedTableGrid}
            onChartEdit={changeSelectedChart}
            readOnly={canvasReadOnly}
            onAddShape={(shape, extras) => {
              addShape(shape, extras);
              setActiveTool(null);
            }}
            onStyle={applySelectedStyle}
            onToggleList={toggleList}
            onApplyTextStyle={applyTextStyleStep}
            onReplaceImage={croppableSelected || (selectedObject && isFillableShape(selectedObject))
              ? () => {
                const target = croppableSelected || selectedObject;
                setLibraryIntent(target && isFillableShape(target) ? 'insert-image' : 'replace');
                openLibrary();
              }
              : undefined}
            onCropImage={croppableSelected ? () => setCroppingId(croppableSelected.id) : undefined}
            onFitImage={croppableSelected && activeSlide
              ? () => {
                const slide = deckRef.current?.slides?.find((item) => item.slideId === activeSlide.slideId);
                if (!slide) return;
                changeObjects(slide.objects.map((object) => (
                  object.id === croppableSelected.id ? fitImageInFrame(object) : object
                )));
              }
              : undefined}
            onToggleMark={toggleSelectedMark}
            queryTextMark={querySelectedMark}
            queryTextStyle={querySelectedStyle}
          />
          )}
          {workspaceMode === 'overview' ? (
            <GridView
              slides={slides}
              selectedIds={gridSelected.length ? gridSelected : [activeSlide.slideId]}
              pageFormat={deck.pageFormat}
              readOnly={readOnly}
              onSelect={(slideId, additive) => setGridSelected((current) => additive ? (current.includes(slideId) ? current.filter((id) => id !== slideId) : [...current, slideId]) : [slideId])}
              onOpen={(slideId) => {
                applySlideSelection({ selectedIds: [slideId], activeId: slideId, rangeAnchorId: slideId });
                setWorkspaceMode('edit');
              }}
              onReorder={handleReorderSlides}
              onSectionTitle={(slideId, title) => {
                replaceSlide(slideId, { sectionTitle: title });
                if (presentationId) void patchSlide(presentationId, slideId, { sectionTitle: title });
              }}
              onExit={() => setWorkspaceMode('edit')}
              showPageKindMarks={!specialMode}
            />
          ) : (
          <Box sx={{ flex: 1, position: 'relative', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {mountedIndexes.includes(activeIndex) && (
              <SlideCanvas
                objects={activeSlide.objects}
                selectedObjectId={selectedObjectId}
                pageFormat={deck.pageFormat}
                advancedMode={advancedMode}
                showGrid={showGrid}
                snap={snap}
                smartGuides={showSmartGuides}
                backgroundColor={activeSlide.backgroundColor || '#FFFFFF'}
                onSelect={selectObject}
                selectedObjectIds={selectedObjectIds}
                isolatedObjectId={isolatedObjectId}
                onBeforeTextContentChange={(object, html) => {
                  if (!activeSlide || !shouldPromptBoundField(object, activeSlide.objects)) return true;
                  setBoundFieldPrompt({ kind: 'edit', objectId: object.id, html });
                  return false;
                }}
                onMakeBoundFieldLocal={specialMode ? undefined : (objectId) => {
                  applyBoundFieldLocal(objectId);
                }}
                onPrepareTextEdit={(objectId) => {
                  if (!activeSlide || canvasReadOnly) return;
                  const object = activeSlide.objects.find((item) => item.id === objectId);
                  if (!object || !isFillableShape(object) || object.style?.fontFamily) return;
                  changeObjects(activeSlide.objects.map((item) => item.id === objectId ? withShapeTextDefaults(item) : item));
                }}
                onBringToFront={() => stackSelected('front')}
                onSendToBack={() => stackSelected('back')}
                onShowInLibrary={revealInLibrary}
                onGroup={groupSelected}
                onUngroup={ungroupSelected}
                onChange={changeObjects}
                onGestureCommit={commitGesture}
                onHistoryBegin={beginHistory}
                onOpenAssets={(objectId) => { selectObject(objectId); openLibrary('replace'); }}
                readOnly={canvasReadOnly || slideLocked}
                onDropAsset={(asset, x, y) => placeAsset(asset, x, y, 'insert-image', { forceInsert: true })}
                onDropText={placeFieldText}
                onDropFiles={canvasReadOnly ? undefined : (file, x, y) => {
                  if (!file) {
                    setError(COPY.unsupportedImageType);
                    return;
                  }
                  void placeLocalImage(file, { x, y, forceInsert: true, failedCopy: COPY.dropImageFailed });
                }}
                croppingId={croppingId}
                onCroppingChange={setCroppingId}
                editingTextId={editingTextId}
                onEditingTextChange={setEditingTextId}
                onTextBoxFit={persistTextBoxFit}
                onTextEditorReady={(live, mounted) => {
                  textEditorRef.current = nextLiveTextEditor(textEditorRef.current, live, mounted);
                }}
                onTextStyle={applySelectedStyle}
                onToggleMark={toggleSelectedMark}
                queryTextMark={querySelectedMark}
              />
            )}
          </Box>
          )}
          <Box sx={{ bgcolor: PRES.white, borderTop: `1px solid ${PRES.line}`, px: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
            {workspaceMode === 'edit' ? (
              <>
                <Typography variant="caption" sx={{ color: PRES.muted, fontWeight: 600, letterSpacing: 0.6 }}>{COPY.notes}</Typography>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={3}
                  value={notes}
                  disabled={readOnly || specialMode}
                  onChange={(event) => persistNotes(event.target.value)}
                  placeholder={COPY.notesPlaceholder}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: PRES.cream } }}
                />
              </>
            ) : (
              <Box sx={{ flex: 1 }} />
            )}
            {canManageTemplates && !libraryMode && (
              <Box sx={{ display: 'flex', bgcolor: PRES.surfaceSunken, borderRadius: 1, p: 0.25, flexShrink: 0 }}>
                {(['simple', 'advanced'] as const).map((mode) => (
                  <Box
                    key={mode}
                    component="button"
                    type="button"
                    onClick={() => setAdvancedMode(mode === 'advanced')}
                    sx={{
                      ...toolButtonSx,
                      border: 0,
                      px: 1.25,
                      color: (mode === 'advanced') === advancedMode ? PRES.white : PRES.textPrimary,
                      bgcolor: (mode === 'advanced') === advancedMode ? PRES.interactive : 'transparent',
                      '&:hover': { bgcolor: (mode === 'advanced') === advancedMode ? PRES.interactiveHover : PRES.canvas },
                    }}
                  >
                    {mode === 'simple' ? COPY.simple : COPY.advanced}
                  </Box>
                ))}
              </Box>
            )}
            <Box sx={{ display: 'flex', bgcolor: PRES.surfaceSunken, borderRadius: 1, p: 0.25, flexShrink: 0 }}>
              {(['edit', 'overview'] as const).map((mode) => (
                <Box
                  key={mode}
                  component="button"
                  type="button"
                  onClick={() => setWorkspaceMode(mode)}
                  sx={{
                    ...toolButtonSx,
                    border: 0,
                    px: 1.25,
                    color: workspaceMode === mode ? PRES.white : PRES.textPrimary,
                    bgcolor: workspaceMode === mode ? PRES.interactive : 'transparent',
                    '&:hover': { bgcolor: workspaceMode === mode ? PRES.interactiveHover : PRES.canvas },
                  }}
                >
                  {mode === 'edit' ? COPY.workspaceEdit : COPY.overview}
                </Box>
              ))}
            </Box>
            {!specialMode && (
            <Button size="small" startIcon={<HistoryIcon />} onClick={() => setHistoryOpen(true)} sx={{ ...toolButtonSx, flexShrink: 0 }}>
              {COPY.history}
            </Button>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', minHeight: 0 }}>
        {!specialMode && originReviewItem && deck.originReview?.items?.length ? (
          <OriginReviewPanel
            review={deck.originReview}
            activeItem={
              deck.originReview.items.find((entry) => (
                entry.slideId === originReviewItem.slideId && entry.objectId === originReviewItem.objectId
              )) || deck.originReview.items[0]
            }
            currentObject={
              slides.find((slide) => slide.slideId === originReviewItem.slideId)?.objects
                .find((object) => object.id === originReviewItem.objectId)
              || selectedObject
            }
            canEdit={canEdit && !readOnly}
            busy={originReviewBusy}
            onSelectItem={(item) => revealOriginReview(item.slideId, item.objectId)}
            onCopy={(item) => { void applyOriginReviewCopy(item); }}
            onDismiss={(item) => { void applyOriginReviewDismiss(item); }}
            onOpenOriginal={(item) => { void openOriginPresentation(item); }}
          />
        ) : null}
        <EditorDock
          tab={dockTab}
          onTab={setDockTab}
          advancedMode={advancedMode || specialMode}
          templateMode={templateMode}
          libraryMode={libraryMode}
          librarySlides={librarySlides}
          objects={activeSlide.objects}
          selectedObject={selectedObject}
          selectedObjectIds={selectedObjectIds}
          slideBackground={activeSlide.backgroundColor || '#FFFFFF'}
          revealAssetId={revealAssetId}
          revealNonce={revealNonce}
          onSelect={selectObject}
          onChangeObject={(patch) => {
            if (!selectedObject) {
              return;
            }
            const nextPatch = { ...patch };
            if (patch.trackingMode === 'pin' && !patch.assetVersionId) {
              nextPatch.assetVersionId = selectedObject.assetVersionId || selectedObject.assetId || null;
            }
            if (patch.trackingMode === 'trackLatest') {
              nextPatch.assetVersionId = null;
            }
            changeObjects(activeSlide.objects.map((object) => object.id === selectedObject.id ? { ...object, ...nextPatch } : object));
          }}
          onStyleObject={applySelectedStyle}
          onChangeObjects={changeObjects}
          onSlideBackground={persistBackground}
          onOpenAssets={() => openLibrary(
            selectedObject && (selectedObject.type === 'image' || selectedObject.type === 'product' || isFillableShape(selectedObject))
              ? (isFillableShape(selectedObject) ? 'insert-image' : 'replace')
              : 'insert-image'
          )}
          libraryIntent={libraryIntent}
          lockedCategoryName={libraryIntent === 'insert-icon' ? 'Ikoner' : null}
          onPlaceAsset={(asset, intent, meta) => placeAsset(asset, 160, 200, intent === 'insert-icon' ? 'insert-image' : intent, { catalogKind: meta?.catalogKind })}
          onToggleList={toggleList}
          onApplyTextStyle={applyTextStyleStep}
          comments={comments}
          canWriteComment={canEdit || canReview}
          canResolveComment={canReview || canApprove}
          onCreateComment={async (body) => {
            if (!activeSlide || !presentationId) return;
            await createPresentationComment(presentationId, {
              slideId: activeSlide.slideId,
              body,
              objectId: selectedObjectId,
            });
            await reloadComments(activeSlide.slideId);
          }}
          onResolveComment={async (id) => {
            if (!presentationId) return;
            await resolvePresentationComment(presentationId, id);
            await reloadComments(activeSlide?.slideId);
          }}
          templates={pickerTemplates}
          pageFormat={deck.pageFormat}
          onAddSlideFromLayout={(layoutId, templateId) => { void addSlideFromLayout(layoutId, templateId); }}
          onAddSlideFromLibrary={requestInsertFromLibrary}
          addingLayout={addingLayout}
          layoutPickerDisabled={readOnly}
          canManageTemplates={canManageTemplates}
          onEditTemplate={onEditTemplate ? (templateId, layoutId) => {
            void (async () => {
              await flushAll();
              onEditTemplate(
                templateId,
                layoutId || previewLayout(
                  pickerTemplates.find((template) => template._id === templateId)?.layouts
                )?.layoutId,
                deck?.name,
              );
            })();
          } : undefined}
          onDuplicateTemplate={canManageTemplates ? async (templateId) => {
            await duplicateTemplate(templateId);
            await reloadPickerTemplates();
          } : undefined}
          onRenameTemplate={canManageTemplates ? async (templateId, name) => {
            await patchTemplate(templateId, { name });
            await reloadPickerTemplates();
          } : undefined}
          onArchiveTemplate={canManageTemplates ? async (templateId) => {
            await patchTemplate(templateId, { isActive: false });
            await reloadPickerTemplates();
          } : undefined}
          onCreateTemplate={canManageTemplates && onEditTemplate ? async (name) => {
            const created = await createTemplate({ name, pageFormat: resolvePageFormat(deck?.pageFormat) });
            await flushAll();
            onEditTemplate(created._id, created.layouts[0]?.layoutId, deck?.name);
          } : undefined}
          onDuplicateLayout={canManageTemplates ? duplicateLayoutInKit : undefined}
          onCopyLayoutToKit={canManageTemplates ? openCopyLayoutToKit : undefined}
          onMoveLayoutToKit={canManageTemplates ? openMoveLayoutToKit : undefined}
          onFieldBinding={(binding: FieldBinding | undefined, placeholder?: string) => {
            if (!selectedObject || !activeSlide) return;
            changeObjects(activeSlide.objects.map((object) => (
              object.id === selectedObject.id
                ? {
                    ...object,
                    fieldBinding: binding,
                    ...(placeholder != null ? { content: placeholder } : {}),
                  }
                : object
            )));
          }}
          globalComponents={globalComponents}
          selectedObjects={selectedObjects}
          canManageGlobals={canManageGlobals}
          onSaveGlobal={async (name) => {
            await createGlobalComponent({ name, objects: selectedObjects });
            setGlobalComponents(await listGlobalComponents());
          }}
          onPlaceGlobal={async (id) => {
            if (!activeSlide || !presentationId) return;
            const slide = await placeGlobalComponent(presentationId, activeSlide.slideId, id, { x: 160, y: 160 });
            replaceSlide(activeSlide.slideId, { objects: slide.objects });
          }}
          onDeleteGlobal={async (id) => {
            await deleteGlobalComponent(id);
            setGlobalComponents(await listGlobalComponents());
          }}
          aiEnabled={isPresentationsAiEnabled(health.aiEnabled)}
          aiBusy={aiBusy}
          onAskAi={(instruction) => applyAiToObject('rewrite', instruction)}
          aiChatMessages={aiChatMessages}
          onAiChat={sendAiChat}
          onInsertAiChat={insertAiChat}
          onClearAiChat={() => setAiChatMessages([])}
          onSetLinkedField={(key, source) => {
            if (!activeSlide || !selectedObject) return;
            const bound = resolveBoundField(activeSlide.objects, selectedObject);
            const productId = selectedObject.type === 'product' ? selectedObject.id : bound?.productId;
            if (!productId) return;
            const localValue = source === 'local' && selectedObject.type !== 'product'
              ? selectedObject.content
              : undefined;
            changeObjects(setLinkedFieldSource(activeSlide.objects, productId, key, source, localValue));
          }}
          onDetachGlobal={canDetachGlobals && selectedObject?.globalComponentId && activeSlide && presentationId
            ? async () => {
              const slide = await detachGlobalComponent(presentationId, activeSlide.slideId, selectedObject.id);
              replaceSlide(activeSlide.slideId, { objects: slide.objects });
            }
            : undefined}
          canDetachGlobal={canDetachGlobals}
          canSaveToLibrary={canSaveToLibrary}
        />
        </Box>
      </Box>
      </DndContext>
      <HealthPanel
        open={healthOpen}
        issues={health.issues}
        readyBlocked={health.readyBlocked}
        aiEnabled={isPresentationsAiEnabled(health.aiEnabled)}
        onClose={() => setHealthOpen(false)}
        onGoTo={(issue) => {
          pendingSelectObjectIdRef.current = issue.objectId || null;
          if (issue.slideId !== activeSlideIdRef.current) {
            applySlideSelection({
              selectedIds: [issue.slideId],
              activeId: issue.slideId,
              rangeAnchorId: issue.slideId,
            });
          } else if (issue.objectId) {
            selectObject(issue.objectId, { isolate: true });
          }
          setHealthOpen(false);
        }}
        onFixWithAi={(issue) => {
          setHealthOpen(false);
          void applyAiToObject('rewrite', undefined, issue.objectId, issue.slideId);
        }}
      />
      <HistoryPanel
        open={historyOpen && !specialMode}
        presentationId={presentationId || ''}
        onClose={() => setHistoryOpen(false)}
        onRestored={(presentation) => { setDeck(presentation); setHistoryOpen(false); }}
      />
      <PdfExportDialog
        open={pdfDialogOpen}
        slides={slides}
        activeSlideId={activeSlide.slideId}
        busy={pdfBusy}
        onClose={() => { if (!pdfBusy) setPdfDialogOpen(false); }}
        onExport={(slideIds) => { void exportPdf(slideIds); }}
      />
      <ImportDialog
        open={importOpen && Boolean(presentationId)}
        presentationId={presentationId || ''}
        kind={importKind}
        onClose={() => setImportOpen(false)}
        onImported={async (summary) => {
          await load();
          setSnack(`${summary.averageConversion}% ${COPY.converted}`);
        }}
      />
      {presenting && (
        <PresenterView
          slides={presentableSlides}
          pageFormat={deck.pageFormat}
          startIndex={presenterStartIndex(slides, activeSlide.slideId)}
          onClose={() => setPresenting(false)}
          onIndexChange={(nextIndex) => {
            const nextSlide = presentableSlides[nextIndex];
            if (nextSlide) {
              applySlideSelection({
                selectedIds: [nextSlide.slideId],
                activeId: nextSlide.slideId,
                rangeAnchorId: nextSlide.slideId,
              });
            }
          }}
        />
      )}
      {printSlide && (
        <Box sx={{ position: 'fixed', left: -20000, top: 0, width: pageSize.width, height: pageSize.height, pointerEvents: 'none', zIndex: -1 }}>
          <StaticSlideView slide={printSlide} variant="print" pageFormat={deck.pageFormat} rootRef={printNodeRef} />
        </Box>
      )}
      <BoundFieldEditDialog
        open={Boolean(boundFieldPrompt)}
        onCancel={() => setBoundFieldPrompt(null)}
        onConfirm={confirmBoundFieldPrompt}
      />
      <Dialog open={Boolean(layoutConfirm)} onClose={() => setLayoutConfirm(null)}>
        <DialogTitle>{COPY.confirm}</DialogTitle>
        <DialogContent>{COPY.confirmLayout}</DialogContent>
        <DialogActions>
          <Button onClick={() => setLayoutConfirm(null)}>{COPY.cancel}</Button>
          <Button
            onClick={async () => {
              const layout = currentTemplate?.layouts.find((item) => item.layoutId === layoutConfirm);
              setLayoutConfirm(null);
              if (!layout || !activeSlide || !presentationId) return;
              await flushAll();
              await createSnapshot(presentationId);
              beginHistory();
              persistObjects(activeSlide.slideId, layout.objects as SlideObject[], true);
              saveQueue.schedule(activeSlide.slideId, { layoutId: layout.layoutId }, true);
              await saveQueue.flushSlide(activeSlide.slideId);
            }}
          >
            {COPY.confirm}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={changeTemplateOpen} onClose={() => setChangeTemplateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{COPY.changeTemplate}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
          <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.body, color: PRES.textSecondary }}>{COPY.changeTemplateHelp}</Typography>
          <Select size="small" value={changeTemplateId} onChange={(event) => setChangeTemplateId(event.target.value)}>
            {templatesMatchingPageFormat(
              templates.filter((template) => template.isActive !== false),
              deck.pageFormat,
            ).map((template) => (
              <MenuItem key={template._id} value={template._id}>{template.name} · {pageFormatLabel(template.pageFormat)}</MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeTemplateOpen(false)} sx={{ textTransform: 'none' }}>{COPY.cancel}</Button>
          <Button
            variant="contained"
            sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none' }}
            disabled={!changeTemplateId || !presentationId}
            onClick={async () => {
              if (!presentationId) return;
              const next = templates.find((template) => template._id === changeTemplateId);
              const matching = (deck.slides || []).some((slide) => next?.layouts.some((layout) => layout.layoutId === slide.layoutId));
              if (next && !matching) setSnack(COPY.changeTemplateNoMatch);
              try {
                await flushAll();
                const updated = await patchPresentation(presentationId, {
                  templateId: changeTemplateId,
                  updateMatchingLayouts: true,
                });
                setDeck(updated);
                setChangeTemplateOpen(false);
              } catch (err: any) {
                setError(err?.response?.data?.message || COPY.saveError);
              }
            }}
          >
            {COPY.confirm}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={renameLayoutOpen} onClose={() => setRenameLayoutOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{COPY.renameLayout}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={COPY.layoutName}
            value={renameLayoutValue}
            onChange={(event) => setRenameLayoutValue(event.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameLayoutOpen(false)} sx={{ textTransform: 'none' }}>{COPY.cancel}</Button>
          <Button
            variant="contained"
            disabled={!renameLayoutValue.trim() || !templateEdit?.templateId || !activeSlide}
            sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none' }}
            onClick={async () => {
              if (!templateEdit?.templateId || !activeSlide) return;
              try {
                await flushAll();
                await patchTemplateLayout(templateEdit.templateId, activeSlide.slideId, { name: renameLayoutValue.trim() });
                await load();
                setRenameLayoutOpen(false);
              } catch (err: any) {
                setError(err?.response?.data?.message || COPY.saveError);
              }
            }}
          >
            {COPY.confirm}
          </Button>
        </DialogActions>
      </Dialog>
      {!specialMode && (
        <LanguageVersionDialog
          open={languageVersionOpen}
          members={familyMembersOf(deck)}
          busy={languageVersionBusy}
          error={languageVersionError}
          onClose={() => {
            if (languageVersionBusy) return;
            setLanguageVersionOpen(false);
            setLanguageVersionError(null);
          }}
          onCreate={submitLanguageVersion}
        />
      )}
      <AddLayoutToKitDialog
        open={Boolean(kitLayoutDialog)}
        mode={kitLayoutDialog?.mode || 'add'}
        templates={templates}
        pageFormat={deck.pageFormat}
        excludeTemplateId={kitLayoutDialog?.mode === 'add' ? null : kitLayoutDialog?.sourceTemplateId}
        name={kitLayoutName}
        onNameChange={setKitLayoutName}
        kitId={kitLayoutKitId}
        onKitChange={setKitLayoutKitId}
        busy={kitLayoutBusy}
        error={kitLayoutError}
        onCancel={() => {
          if (kitLayoutBusy) return;
          setKitLayoutDialog(null);
          setKitLayoutError(null);
        }}
        onConfirm={(openAfter) => { void submitKitLayoutDialog(openAfter); }}
      />
      <LibraryInsertDialog
        open={Boolean(insertLibraryId)}
        name={librarySlides.find((item) => item._id === insertLibraryId)?.name}
        busy={addingLayout}
        onCancel={() => setInsertLibraryId(null)}
        onChoose={(follow) => {
          if (insertLibraryId) void addSlideFromLibraryPage(insertLibraryId, follow);
        }}
      />
      <LibraryUpdatesDialog
        updates={libraryUpdates}
        onClose={() => setLibraryUpdates([])}
        onOpen={(slideId) => {
          applySlideSelection({ selectedIds: [slideId], activeId: slideId, rangeAnchorId: slideId });
        }}
      />
      {error && <Alert severity="error" sx={{ position: 'absolute', bottom: 16, left: 16 }} onClose={() => setError(null)}>{error}</Alert>}
      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={aiBusy ? null : (pdfObjectUrl && snack?.includes('PDF klar') ? 20000 : 4000)}
        onClose={() => setSnack(null)}
        message={snack}
        action={pdfObjectUrl && snack?.includes('PDF klar') ? (
          <Button color="inherit" size="small" onClick={() => window.open(pdfObjectUrl, '_blank')}>
            Öppna PDF
          </Button>
        ) : undefined}
      />
    </Box>
  );
};

const FormLikeSnap: React.FC<{
  showGrid: boolean;
  snap: boolean;
  setShowGrid: (value: boolean) => void;
  setSnap: (value: boolean) => void;
}> = ({ showGrid, snap, setShowGrid, setSnap }) => (
  <>
    <Button
      size="small"
      onClick={() => setShowGrid(!showGrid)}
      sx={{ textTransform: 'none', bgcolor: showGrid ? PRES.navy : PRES.white, color: showGrid ? PRES.white : PRES.navy, border: `1px solid ${PRES.line}`, minHeight: 32 }}
    >
      Rutnät
    </Button>
    <Button
      size="small"
      onClick={() => setSnap(!snap)}
      sx={{ textTransform: 'none', bgcolor: snap ? PRES.navy : PRES.white, color: snap ? PRES.white : PRES.navy, border: `1px solid ${PRES.line}`, minHeight: 32 }}
    >
      Fäst
    </Button>
  </>
);

export default PresentationsEditor;
